import { newWidgetItemId } from "./widget-item-id.js";
import { EscalationMatrixExpandedEditor } from "./escalationMatrix/ExpandedEditor.js";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefCallback,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  ChartNoAxesCombined,
  Check,
  GripVertical,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Table2,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button, StatusDot, Switch } from "../atoms/index.js";
import type { StatusTone } from "../lib/tones.js";
import { Tooltip } from "../molecules/Tooltip/Tooltip.js";
import {
  DataTable as DsDataTable,
  type DataTableColumn,
} from "../molecules/DataTable/DataTable.js";
import { EmptyState } from "../molecules/EmptyState/EmptyState.js";
import type { WidgetKind } from "@playbookai/types";
import type { WidgetChangeOptions } from "./types.js";
import type {
  CausalChainParams,
  ControlPlanParams,
  CountermeasureTableParams,
  EscalationMatrixParams,
  FishboneParams,
  FunnelParams,
  ImpactEffortParams,
  KpiTileParams,
  KpiTileLegacyData,
  KpiTileRange,
  ParetoParams,
  ProblemTimelineParams,
  ContainmentPlanParams,
  ProblemTreeParams,
  RunChartParams,
  StakeholderMapParams,
} from "@playbookai/widget-schemas";
import {
  widgetTargetKey,
  DEFAULT_KPI_TILE_RANGE,
  KPI_TILE_RANGES,
} from "@playbookai/widget-schemas";
import { cn } from "../lib/utils.js";
import {
  useFieldEditSession,
  useFieldEditSessionMap,
} from "./field-edit-session.js";
import { Button as DsButton } from "../atoms/Button/index.js";
import { Input as DsInput } from "../atoms/Input/index.js";
import { Textarea as DsTextarea } from "../atoms/Textarea/index.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../molecules/Popover/Popover.js";
import { ProblemTreeExpandedEditor } from "./problemTree/ExpandedEditor.js";
import { RunChartExpandedEditor } from "./runChart/ExpandedEditor.js";
import { KpiTileWidget } from "./kpiTile/Component.js";
import { KPI_TILE_RANGE_LABEL } from "./kpiTile/def.js";
import { KpiPickList } from "./KpiPickList.js";
import { useKpiCatalog } from "./kpi-catalog.js";
import { ParetoBody, type ParetoAccent } from "./paretoL1/Component.js";
import { FishboneWidget } from "./fishbone/Component.js";
import {
  ImpactEffortWidget,
  tierFor,
  type ImpactEffortOverlapMode,
  type ImpactEffortScale,
  type Tier,
} from "./impactEffort/Component.js";
import {
  impactEffortDisplayYPctToScoreYPct,
  impactEffortPillSize,
} from "./impactEffort/fan-out.js";
import { CountermeasureExpandedEditor } from "./countermeasureTable/ExpandedEditor.js";
import {
  StakeholderAddForm,
  StakeholderMapWidget,
  type StakeholderDraft,
} from "./stakeholderMap/Component.js";
import { ControlPlanExpandedEditor } from "./controlPlan/ExpandedEditor.js";
import { FunnelWidget } from "./funnel/Component.js";
import {
  CausalChainWidget,
  type CausalChainSelection,
} from "./causalChain/Component.js";
import { ProblemTimelineEditor } from "./problemTimeline/Editor.js";
import { ContainmentPlanEditor } from "./containmentPlan/Editor.js";

interface ExpandedWidgetEditorProps {
  readonly kind: WidgetKind;
  readonly data: unknown;
  readonly onChange: (
    next: unknown,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  readonly readOnly?: boolean | undefined;
}

export function ExpandedWidgetEditor({
  kind,
  data,
  onChange,
  readOnly,
}: ExpandedWidgetEditorProps): ReactElement | null {
  switch (kind) {
    case "runChart":
      return (
        <RunChartExpandedEditor
          data={data as RunChartParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "kpiTile":
      return (
        <KpiTileEditor
          data={data as KpiTileParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "problemTree":
      return (
        <ProblemTreeExpandedEditor
          data={data as ProblemTreeParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "paretoL1":
    case "paretoL2":
      return (
        <ParetoEditor
          data={data as ParetoParams}
          onChange={onChange}
          readOnly={readOnly}
          accent={kind === "paretoL1" ? "purple" : "cyan"}
        />
      );
    case "funnel":
      return (
        <FunnelEditor
          data={data as FunnelParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "causalChain":
      return (
        <CausalChainEditor
          data={data as CausalChainParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "fishbone":
      return (
        <FishboneEditor
          data={data as FishboneParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "impactEffort": {
      const impactEffort = data as ImpactEffortParams;
      const parked = new Set(impactEffort.excludedIds ?? []);
      return (
        <ImpactEffortExpandedEditor
          data={impactEffort}
          onChange={onChange}
          readOnly={readOnly}
          parkingItems={impactEffort.points.map((point) => ({
            id: point.id,
            label: point.label,
            parked: parked.has(point.id),
            point,
          }))}
          onParkingChange={(id, isParked) => {
            const next = new Set(impactEffort.excludedIds ?? []);
            if (isParked) next.add(id);
            else next.delete(id);
            onChange({ ...impactEffort, excludedIds: [...next] });
          }}
          onPlaceFromParking={(id, scores) => {
            const nextExcludedIds = (impactEffort.excludedIds ?? []).filter(
              (excludedId) => excludedId !== id,
            );
            onChange({
              ...impactEffort,
              excludedIds: nextExcludedIds,
              points: impactEffort.points.map((point) =>
                point.id === id ? { ...point, ...scores } : point,
              ),
            });
          }}
          onAddToParking={(label) => {
            const id = uid("idea");
            onChange({
              ...impactEffort,
              points: [
                ...impactEffort.points,
                { id, label, impact: 5, effort: 5 },
              ],
              excludedIds: [...(impactEffort.excludedIds ?? []), id],
            });
          }}
          /* PB-4551: a Kaizen's name is editable in place. This path is the
             widget's OWN `onChange`, so only the point's `label` changes —
             its coordinates, quadrant and parked state are carried through
             untouched, which is the whole difference from the delete-and-
             recreate the reporter had to do. */
          onRenameItem={(id, label) => {
            onChange({
              ...impactEffort,
              points: impactEffort.points.map((point) =>
                point.id === id ? { ...point, label } : point,
              ),
            });
          }}
        />
      );
    }
    case "countermeasureTable":
      return (
        <CountermeasureEditor
          data={data as CountermeasureTableParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "stakeholderMap":
      return (
        <StakeholderMapEditor
          data={data as StakeholderMapParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "controlPlan":
      return (
        <ControlPlanEditor
          data={data as ControlPlanParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "escalationMatrix":
      return (
        <EscalationMatrixEditor
          data={data as EscalationMatrixParams}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case "problemTimeline":
      return (
        <ProblemTimelineEditor
          data={data as ProblemTimelineParams}
          onChange={onChange}
          readOnly={readOnly ?? false}
        />
      );
    case "containmentPlan":
      return (
        <ContainmentPlanEditor
          data={data as ContainmentPlanParams}
          onChange={onChange}
          readOnly={readOnly ?? false}
        />
      );
    default:
      return null;
  }
}

export function hasExpandedWidget(kind: WidgetKind): boolean {
  return [
    "runChart",
    "kpiTile",
    "problemTree",
    "paretoL1",
    "paretoL2",
    "funnel",
    "causalChain",
    "fishbone",
    "impactEffort",
    "countermeasureTable",
    "stakeholderMap",
    "controlPlan",
    "escalationMatrix",
    "problemTimeline",
    "containmentPlan",
  ].includes(kind);
}

// Kept (currently unwired) — superseded by the inline editor but retained as
// the reference implementation. Uses hooks, so the name must stay a valid
// component name (rules-of-hooks); unused-vars disabled with this reason.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function RunChartEditor({
  data,
  onChange,
  readOnly,
}: {
  data: RunChartParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const setScalar = (key: "title" | "unit", value: string): void => {
    onChange({ ...data, [key]: value });
  };
  const setTarget = (raw: string): void => {
    const next: RunChartParams = { ...data };
    if (raw.trim() === "") {
      delete next.target;
    } else {
      next.target = Number(raw);
    }
    onChange(next);
  };
  const updatePoint = (
    index: number,
    patch: Partial<RunChartParams["points"][number]>,
  ): void => {
    onChange({
      ...data,
      points: data.points.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    });
  };
  const addRow = (): void => {
    onChange({
      ...data,
      points: [
        ...data.points,
        {
          id: newWidgetItemId("point"),
          label: `Row ${data.points.length + 1}`,
          value: 0,
        },
      ],
    });
  };
  const parseCsv = async (file: File): Promise<void> => {
    const text = await file.text();
    const rows = text
      .split(/\r?\n/)
      .map((line) => line.split(",").map((c) => c.trim()))
      .filter((cols) => cols.length >= 2 && cols[0]);
    const points = rows
      .map(([label, value]) => ({
        id: newWidgetItemId("point"),
        label: label ?? "",
        value: Number(value),
      }))
      .filter((p) => p.label && Number.isFinite(p.value));
    if (points.length > 0) onChange({ ...data, points });
  };

  return (
    <EditorShell
      readOnly={readOnly}
      actions={
        <>
          <IconButton
            disabled={readOnly}
            onClick={() => fileRef.current?.click()}
            label="Upload CSV"
          >
            <Upload className="w-3.5 h-3.5" /> Upload
          </IconButton>
          <IconButton disabled={readOnly} onClick={addRow} label="Add row">
            <Plus className="w-3.5 h-3.5" /> Add row
          </IconButton>
          {/* eslint-disable-next-line ds/no-hand-built -- hidden native file picker behind the Upload button; the design system has no picker-only part */}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void parseCsv(file);
              e.currentTarget.value = "";
            }}
          />
        </>
      }
    >
      <div className="grid grid-cols-3 gap-3">
        <Field label="Chart title">
          <TextInput
            value={data.title ?? ""}
            onChange={(v) => setScalar("title", v)}
            disabled={readOnly}
            targetKey={propTarget("title")}
          />
        </Field>
        <Field label="Y-axis / unit">
          <TextInput
            value={data.unit ?? ""}
            onChange={(v) => setScalar("unit", v)}
            disabled={readOnly}
            targetKey={propTarget("unit")}
          />
        </Field>
        <Field label="Target">
          <TextInput
            value={data.target == null ? "" : String(data.target)}
            onChange={setTarget}
            disabled={readOnly}
            targetKey={propTarget("target")}
          />
        </Field>
      </div>
      <DataTable headers={["Label", "Value", ""]}>
        {data.points.map((point, index) => (
          <tr key={point.id ?? `${point.label}-${index}`}>
            <td>
              <TextInput
                value={point.label}
                onChange={(v) => updatePoint(index, { label: v })}
                disabled={readOnly}
                // A point minted before the identity migration has no id, so it
                // is unaddressable and correctly leases nothing rather than
                // claiming a target the executor could never match.
                {...(point.id === undefined
                  ? {}
                  : {
                      targetKey: itemFieldTarget("points", point.id, "label"),
                    })}
              />
            </td>
            <td>
              <TextInput
                value={String(point.value)}
                onChange={(v) => updatePoint(index, { value: Number(v) })}
                disabled={readOnly}
                {...(point.id === undefined
                  ? {}
                  : {
                      targetKey: itemFieldTarget("points", point.id, "value"),
                    })}
              />
            </td>
            <td className="w-10">
              <TrashButton
                disabled={readOnly}
                onClick={() =>
                  onChange({
                    ...data,
                    points: data.points.filter((_, i) => i !== index),
                  })
                }
              />
            </td>
          </tr>
        ))}
      </DataTable>
    </EditorShell>
  );
}

/** A number still being typed: a lone sign, a bare or trailing decimal point. */
const TARGET_PARTIAL = /^[-+]?(\d+\.?\d*|\.\d*)?$/;
/** …or an exponent whose digits have not arrived yet. Needs a mantissa, so a
 *  lone "e" is a typo rather than a number in progress. */
const TARGET_PARTIAL_EXPONENT = /^[-+]?(\d+\.?\d*|\.\d+)[eE][-+]?$/;

/**
 * PB-1559: a typed target is a number, and a caller that declines to commit
 * anything else must SAY so rather than silently reverting on blur. Says it
 * without complaining about a value that is merely unfinished.
 *
 * Leads with the *same* predicate a caller commits on, so the two cannot
 * disagree about anything committable — a hand-written pattern claiming to
 * mirror `Number()` drifts from it. It did: an earlier version rejected "+1",
 * which `Number` reads as 1 and the caller happily stored, so the field showed
 * an error over a value it had just accepted. The patterns below only widen the
 * silence to text a number is typed *through*; they never narrow it.
 *
 * Kept (currently unwired) — same convention as `ProblemTreeEditor` below. Its
 * one consumer was the KPI Tile's target field, which went when KPI Manager
 * became the authority for the target; `DraftField`'s `validate` contract is a
 * documented design-system capability (packages/ui/CLAUDE.md) and the next
 * numeric expanded-editor field is its home. Re-homing it on Run Chart's target,
 * which has the same silent-drop shape, is deliberately out of this change.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function validateTarget(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  if (Number.isFinite(Number(trimmed))) return null;
  if (TARGET_PARTIAL.test(trimmed)) return null;
  if (TARGET_PARTIAL_EXPONENT.test(trimmed)) return null;
  return "Target must be a number.";
}

/**
 * KPI Tile — choose the KPI, and decide how it is presented. Nothing here edits
 * a measurement: KPI Manager owns the value, the target and the history, and
 * the preview below renders exactly what the canvas card renders, resolved
 * through the same host seam.
 */
function KpiTileEditor({
  data,
  onChange,
  readOnly,
}: {
  data: KpiTileParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  const catalog = useKpiCatalog();
  const activateCatalog = catalog?.activate;
  useEffect(() => {
    activateCatalog?.(data.kpiId ? [data.kpiId] : []);
  }, [activateCatalog, data.kpiId]);

  const selectKpi = (kpiId: string): void => {
    // Choosing a KPI is what RETIRES the preserved snapshot: the schema refuses
    // a tile that claims both, and keeping it would leave two answers on one
    // card. Nothing is written to KPI Manager — this only records the choice.
    const next: KpiTileParams = { ...data, kpiId };
    delete next.legacy;
    onChange(next);
  };

  return (
    <div
      data-testid="kpi-expanded-layout"
      className="grid flex-1 min-h-0 grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-5"
    >
      <div
        data-testid="kpi-expanded-fields"
        className="min-h-0 overflow-auto pe-1 space-y-3"
      >
        <fieldset disabled={readOnly}>
          <legend className={fieldLabelClass}>KPI</legend>
          <KpiPickList
            name="pb-kpi-tile-source"
            selectedId={data.kpiId}
            onSelect={selectKpi}
            emptyHint="No KPI you can access matches this search."
          />
        </fieldset>
        {/* PB-4923 AC5: the caption names the merge-register field it edits, so
            focusing it claims a per-field editing session. The tile is flat
            (merge-map `fields`), so the target key is the property name. */}
        <Field label="Caption (blank uses the KPI's own name)">
          <TextInput
            value={data.label}
            onChange={(v) => onChange({ ...data, label: v })}
            disabled={readOnly}
            targetKey="label"
          />
        </Field>
        <Field label="Period shown">
          <select
            value={data.range ?? DEFAULT_KPI_TILE_RANGE}
            onChange={(event) =>
              onChange({
                ...data,
                range: event.target.value as KpiTileRange,
              })
            }
            disabled={readOnly}
            className={inputClass}
          >
            {KPI_TILE_RANGES.map((range) => (
              <option key={range} value={range}>
                {KPI_TILE_RANGE_LABEL[range]}
              </option>
            ))}
          </select>
        </Field>
        {data.legacy ? (
          <div className="rounded-md border border-pb-amber/40 bg-pb-amber-soft/40 p-3 space-y-1">
            <p className="text-dense-1 font-semibold text-pb-amber-text uppercase tracking-wider">
              Previously typed values
            </p>
            <p className="text-dense-1 text-pb-text-muted">
              Kept exactly as they were, and not editable:{" "}
              {legacySummary(data.legacy)}. Choosing a KPI above replaces them
              and removes this note. Nothing here is written back to KPI
              Manager.
            </p>
          </div>
        ) : null}
      </div>
      <div
        data-testid="kpi-expanded-preview"
        className="h-full min-h-0 rounded-lg border border-pb-border bg-pb-card-elevated/30 p-5 flex flex-col"
      >
        <div className="text-dense-1 uppercase tracking-wider font-semibold text-pb-text-muted mb-3">
          Preview
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          <KpiTileWidget
            data={data}
            onChange={() => undefined}
            readOnly
            showDetail
          />
        </div>
      </div>
    </div>
  );
}

/** One line describing what a pre-binding tile used to say. */
function legacySummary(legacy: KpiTileLegacyData): string {
  const unit = legacy.unit?.trim() ? ` ${legacy.unit.trim()}` : "";
  const value =
    legacy.value.trim() === "" ? "no value" : `${legacy.value}${unit}`;
  const target =
    legacy.target == null ? "" : `, target ${legacy.target}${unit}`;
  const history =
    legacy.recentValues.length === 0
      ? ""
      : `, ${legacy.recentValues.length} recorded value${legacy.recentValues.length === 1 ? "" : "s"}`;
  return `${value}${target}${history}`;
}

// Kept (currently unwired) — see note on RunChartEditor above.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ProblemTreeEditor({
  data,
  onChange,
  readOnly,
}: {
  data: ProblemTreeParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  return (
    <EditorShell
      readOnly={readOnly}
      actions={
        <IconButton
          disabled={readOnly}
          onClick={() =>
            onChange({
              effects: [
                { id: uid("effect"), text: "Symptom 1" },
                { id: uid("effect"), text: "Symptom 2" },
                { id: uid("effect"), text: "Symptom 3" },
              ],
              problem: "Problem statement",
              causes: [
                { id: uid("cause"), text: "Cause 1" },
                { id: uid("cause"), text: "Cause 2" },
                { id: uid("cause"), text: "Cause 3" },
              ],
            })
          }
          label="Reset tree"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </IconButton>
      }
    >
      <Field label="Problem statement">
        <TextInput
          value={data.problem}
          onChange={(v) => onChange({ ...data, problem: v })}
          disabled={readOnly}
          targetKey={propTarget("problem")}
        />
      </Field>
      <ListEditor
        title="Effects"
        addLabel="Add Effect"
        values={data.effects}
        disabled={readOnly}
        placeholderPrefix="Symptom"
        collection="effects"
        onChange={(effects) => onChange({ ...data, effects })}
      />
      <ListEditor
        title="Root causes"
        addLabel="Add Root Cause"
        values={data.causes}
        disabled={readOnly}
        placeholderPrefix="Cause"
        collection="causes"
        onChange={(causes) => onChange({ ...data, causes })}
      />
    </EditorShell>
  );
}

export function ParetoEditor({
  data,
  onChange,
  readOnly,
  accent,
}: {
  data: ParetoParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
  accent: ParetoAccent;
}): ReactElement {
  const bars = [...data.bars].sort((a, b) => b.value - a.value);
  const total = bars.reduce((sum, candidate) => sum + candidate.value, 0);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const focusPendingInput: RefCallback<HTMLInputElement> = (element) => {
    if (!element) return;
    element.focus();
    element.select();
    setPendingFocusId(null);
  };
  const updateBar = (
    id: string,
    patch: Partial<ParetoParams["bars"][number]>,
  ): void => {
    onChange({
      ...data,
      bars: data.bars.map((bar) =>
        bar.id === id ? { ...bar, ...patch } : bar,
      ),
    });
  };
  const addBar = (): void => {
    if (readOnly || data.bars.length >= 50) return;
    const id = uid("bar");
    setPendingFocusId(id);
    onChange({
      ...data,
      bars: [...data.bars, { id, label: "New driver", value: 0 }],
    });
  };
  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-auto pe-1">
      <div
        data-testid="pareto-expanded-chart"
        className="flex h-64 min-h-64 flex-col"
      >
        <ParetoBody data={data} accent={accent} />
      </div>
      <ParetoDetailsPanel data={data} readOnly={readOnly} onChange={onChange} />
      <ParetoDataGrid
        data={data}
        bars={bars}
        total={total}
        readOnly={readOnly}
        pendingFocusId={pendingFocusId}
        pendingInputRef={focusPendingInput}
        onAdd={addBar}
        onChange={onChange}
        onUpdateBar={updateBar}
      />
    </div>
  );
}

function ParetoDetailsPanel({
  data,
  readOnly,
  onChange,
}: {
  readonly data: ParetoParams;
  readonly readOnly?: boolean | undefined;
  readonly onChange: (next: unknown) => void;
}): ReactElement {
  return (
    <section
      data-testid="pareto-chart-details"
      className="space-y-3 rounded-lg border border-pb-border bg-pb-card-elevated/30 p-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-pb-text">
          Chart details
        </h3>
        {readOnly ? (
          <span className="text-dense-1 font-medium text-pb-text-muted">
            Read-only
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-[minmax(0,3fr)_minmax(6rem,1fr)] gap-3">
        <Field label="Title">
          <TextInput
            value={data.title ?? ""}
            onChange={(value) => onChange({ ...data, title: value })}
            disabled={readOnly}
            targetKey={propTarget("title")}
          />
        </Field>
        <Field label="Unit">
          <TextInput
            value={data.unit ?? ""}
            onChange={(value) => onChange({ ...data, unit: value })}
            disabled={readOnly}
            targetKey={propTarget("unit")}
          />
        </Field>
      </div>
    </section>
  );
}

function ParetoDataGrid({
  data,
  bars,
  total,
  readOnly,
  pendingFocusId,
  pendingInputRef,
  onAdd,
  onChange,
  onUpdateBar,
}: {
  readonly data: ParetoParams;
  readonly bars: ParetoParams["bars"];
  readonly total: number;
  readonly readOnly?: boolean | undefined;
  readonly pendingFocusId: string | null;
  readonly pendingInputRef: RefCallback<HTMLInputElement>;
  readonly onAdd: () => void;
  readonly onChange: (next: unknown) => void;
  readonly onUpdateBar: (
    id: string,
    patch: Partial<ParetoParams["bars"][number]>,
  ) => void;
}): ReactElement {
  const cumulativePercentById = new Map<string, number | null>();
  let running = 0;
  for (const bar of bars) {
    running += bar.value;
    cumulativePercentById.set(
      bar.id,
      total > 0 ? Math.round((running / total) * 100) : null,
    );
  }
  const columns: DataTableColumn<ParetoParams["bars"][number]>[] = [
    {
      id: "driver",
      header: "Driver",
      width: "48%",
      minWidth: 256,
      cell: (bar) => (
        <ParetoLabelInput
          inputRef={bar.id === pendingFocusId ? pendingInputRef : undefined}
          ariaLabel={`Driver ${bars.indexOf(bar) + 1}`}
          value={bar.label}
          disabled={readOnly}
          targetKey={itemFieldTarget("bars", bar.id, "label")}
          onChange={(value) => onUpdateBar(bar.id, { label: value })}
        />
      ),
    },
    {
      id: "value",
      header: data.unit?.trim() ? `Value (${data.unit.trim()})` : "Value",
      width: "22%",
      minWidth: 120,
      cell: (bar) => (
        <ParetoValueInput
          value={bar.value}
          label={bar.label}
          disabled={readOnly}
          targetKey={itemFieldTarget("bars", bar.id, "value")}
          onCommit={(value) => onUpdateBar(bar.id, { value })}
        />
      ),
    },
    {
      id: "cumulative",
      header: "Cumulative",
      width: "24%",
      minWidth: 128,
      cell: (bar) => {
        const cumulativePercent = cumulativePercentById.get(bar.id) ?? null;
        return (
          <div className="flex items-center gap-2">
            <span className="w-8 shrink-0 text-end text-xs tabular-nums text-pb-text-muted">
              {cumulativePercent == null ? "—" : `${cumulativePercent}%`}
            </span>
            <span
              className="hidden h-1.5 flex-1 overflow-hidden rounded-full bg-pb-border sm:block"
              aria-hidden
            >
              <span
                className="block h-full rounded-full bg-pb-text-muted/60"
                style={{ width: `${cumulativePercent ?? 0}%` }}
              />
            </span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: "6%",
      minWidth: 40,
      align: "center",
      cell: (bar) =>
        !readOnly ? (
          <TrashButton
            label={`Delete driver ${bar.label}`}
            // Shown on row hover or focus only, as before the DataTable swap;
            // the DataTable row carries no group class, so the row is matched
            // by element.
            className="opacity-0 transition-opacity [tr:focus-within_&]:opacity-100 [tr:hover_&]:opacity-100"
            onClick={() =>
              onChange({
                ...data,
                bars: data.bars.filter((candidate) => candidate.id !== bar.id),
              })
            }
          />
        ) : null,
    },
  ];
  return (
    <section
      data-testid="pareto-data-editor"
      className="overflow-hidden rounded-lg border border-pb-border bg-pb-card-elevated/20"
    >
      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
        <div className="flex min-w-0 items-baseline gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-pb-text">
            Data
          </h3>
          <span className="text-dense-1 text-pb-text-muted">
            {bars.length} driver{bars.length === 1 ? "" : "s"}
          </span>
        </div>
        {!readOnly ? (
          <Button
            variant="outline"
            size="sm"
            disabled={bars.length >= 50}
            aria-label="Add driver"
            onClick={onAdd}
          >
            <Plus className="h-3.5 w-3.5" /> Add driver
          </Button>
        ) : null}
      </div>
      {/* Layout only: the DataTable's own box is the scroller, capped at
          max-h-72 (a flex column lets it shrink), so its sticky header stays
          put while rows scroll. The negative margins lay its side and bottom
          edges on the panel's border, and its top edge is the divider under
          the heading (L-27). */}
      <DsDataTable
        caption="Pareto drivers, values, and cumulative percentages"
        className="-mx-px -mb-px flex max-h-72 w-auto flex-col"
        layout="fixed"
        density="compact"
        columns={columns}
        data={bars}
        getRowId={(bar) => bar.id}
        emptyState={
          <EmptyState
            title="No drivers yet"
            description="Add a driver to build this Pareto."
          />
        }
      />
    </section>
  );
}

/**
 * The Pareto grid's label cell: the ghost design-system Input, the grid's cell
 * editor. It carries a focus ref for the just-added row, and PB-4923's lease
 * is composed onto its handlers here.
 */
function ParetoLabelInput({
  value,
  ariaLabel,
  disabled,
  targetKey,
  inputRef,
  onChange,
}: {
  readonly value: string;
  readonly ariaLabel: string;
  readonly disabled?: boolean | undefined;
  readonly targetKey: string;
  readonly inputRef: RefCallback<HTMLInputElement> | undefined;
  readonly onChange: (value: string) => void;
}): ReactElement {
  const lease = useFieldEditSession(targetKey);
  return (
    <DsInput
      ref={inputRef}
      variant="ghost"
      size="sm"
      aria-label={ariaLabel}
      maxLength={255}
      value={value}
      disabled={disabled}
      onFocus={lease.onFocus}
      onBlur={lease.onBlur}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function ParetoValueInput({
  value,
  label,
  disabled,
  targetKey,
  onCommit,
}: {
  readonly value: number;
  readonly label: string;
  readonly disabled?: boolean | undefined;
  readonly targetKey: string;
  readonly onCommit: (value: number) => void;
}): ReactElement {
  const lease = useFieldEditSession(targetKey);
  const canonicalValue = String(value);
  const [draft, setDraft] = useState(canonicalValue);
  const focusedRef = useRef(false);
  const cancelBlurRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setDraft(canonicalValue);
  }, [canonicalValue]);
  const commit = (): void => {
    focusedRef.current = false;
    // Release BEFORE the value round-trips: the person has left the field, so
    // holding the claim through the save would keep an approved AI change away
    // from a target nobody is in front of any more.
    lease.onBlur();
    if (cancelBlurRef.current) {
      cancelBlurRef.current = false;
      setDraft(canonicalValue);
      return;
    }
    const trimmed = draft.trim();
    const next = trimmed === "" ? 0 : Number(trimmed);
    if (!Number.isFinite(next) || next < 0) {
      setDraft(canonicalValue);
      return;
    }
    setDraft(String(next));
    if (next !== value) onCommit(next);
  };
  return (
    // Fixed-width digits, like the Cumulative column beside it. The field
    // inherits them (preflight gives inputs `font: inherit`); `contents` keeps
    // the wrapper out of the layout.
    <div className="contents tabular-nums">
      <DsInput
        variant="ghost"
        size="sm"
        aria-label={`Value for ${label}`}
        inputMode="decimal"
        value={draft}
        disabled={disabled}
        onFocus={() => {
          focusedRef.current = true;
          lease.onFocus();
        }}
        onBlur={commit}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            cancelBlurRef.current = true;
            setDraft(canonicalValue);
            event.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}

function FunnelEditor({
  data,
  onChange,
  readOnly,
}: {
  data: FunnelParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  const updateLevel = (
    id: string,
    patch: Partial<FunnelParams["levels"][number]>,
  ): void => {
    onChange({
      ...data,
      levels: data.levels.map((level) =>
        level.id === id ? { ...level, ...patch } : level,
      ),
    });
  };
  return (
    <div
      data-testid="funnel-expanded-preview"
      className="flex-1 min-h-0 flex flex-col"
    >
      <FunnelWidget
        data={data}
        onChange={() => undefined}
        readOnly={readOnly === true}
        expanded
        onLevelTitleChange={(levelId, title) => updateLevel(levelId, { title })}
        onItemTextChange={(levelId, itemId, text) => {
          const level = data.levels.find((entry) => entry.id === levelId);
          if (!level) return;
          updateLevel(levelId, {
            items: level.items.map((item) =>
              item.id === itemId ? { ...item, text } : item,
            ),
          });
        }}
        onAddItem={(levelId, text) => {
          const level = data.levels.find((entry) => entry.id === levelId);
          if (!level) return;
          updateLevel(levelId, {
            items: [...level.items, { id: uid("item"), text }],
          });
        }}
        onRemoveItem={(levelId, itemId) => {
          const level = data.levels.find((entry) => entry.id === levelId);
          if (!level) return;
          updateLevel(levelId, {
            items: level.items.filter((item) => item.id !== itemId),
          });
        }}
      />
    </div>
  );
}

function CausalChainEditor({
  data,
  onChange,
  readOnly,
}: {
  data: CausalChainParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  const [selection, setSelection] = useState<CausalChainSelection | null>(() =>
    data.problem
      ? { kind: "problem" }
      : data.steps.length > 0
        ? { kind: "step", index: 0 }
        : null,
  );
  const maxSteps = 20;
  const activeSelection: CausalChainSelection | null =
    selection?.kind === "problem"
      ? data.problem !== undefined
        ? selection
        : data.steps.length > 0
          ? { kind: "step", index: 0 }
          : null
      : selection?.kind === "step" && data.steps[selection.index]
        ? selection
        : data.problem !== undefined
          ? { kind: "problem" }
          : data.steps.length > 0
            ? { kind: "step", index: 0 }
            : null;
  const rootIdentified = data.steps.some((step) => step.isRoot === true);
  const whyLabel = `${data.steps.length} ${data.steps.length === 1 ? "Why" : "Whys"} explored`;

  const addProblem = (): void => {
    if (readOnly) return;
    onChange({ ...data, problem: "Describe the problem" });
    setSelection({ kind: "problem" });
  };
  const removeProblem = (): void => {
    if (readOnly) return;
    const { problem: _problem, ...next } = data;
    onChange(next);
    setSelection(data.steps.length > 0 ? { kind: "step", index: 0 } : null);
  };
  const insertWhy = (index: number): void => {
    if (readOnly || data.steps.length >= maxSteps) return;
    const steps = [...data.steps];
    // PB-4923: a step needs an id to be addressable by a bounded operation.
    steps.splice(index, 0, { id: uid("step"), label: "New cause", body: "" });
    onChange({ ...data, steps });
    setSelection({ kind: "step", index });
  };
  const updateStep = (
    index: number,
    patch: Partial<CausalChainParams["steps"][number]>,
  ): void => {
    if (readOnly) return;
    onChange({
      ...data,
      steps: data.steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, ...patch } : step,
      ),
    });
  };
  const removeStep = (index: number): void => {
    if (readOnly) return;
    const steps = data.steps.filter((_, stepIndex) => stepIndex !== index);
    onChange({ ...data, steps });
    if (steps.length > 0) {
      setSelection({ kind: "step", index: Math.min(index, steps.length - 1) });
    } else {
      setSelection(data.problem !== undefined ? { kind: "problem" } : null);
    }
  };
  const moveStep = (index: number, targetIndex: number): void => {
    if (
      readOnly ||
      targetIndex < 0 ||
      targetIndex >= data.steps.length ||
      index === targetIndex
    ) {
      return;
    }
    const steps = [...data.steps];
    const [step] = steps.splice(index, 1);
    if (!step) return;
    steps.splice(targetIndex, 0, step);
    onChange({ ...data, steps });
    setSelection({ kind: "step", index: targetIndex });
  };
  const setRoot = (index: number, checked: boolean): void => {
    if (readOnly) return;
    onChange({
      ...data,
      steps: data.steps.map((step, stepIndex) => {
        const { isRoot: _isRoot, ...withoutRoot } = step;
        if (stepIndex === index) {
          return checked ? { ...withoutRoot, isRoot: true } : withoutRoot;
        }
        if (checked && step.isRoot) return withoutRoot;
        return step;
      }),
    });
  };

  const selectedStep =
    activeSelection?.kind === "step"
      ? data.steps[activeSelection.index]
      : undefined;
  return (
    <div
      data-testid="causal-chain-expanded-preview"
      className="flex-1 min-h-0 flex flex-col gap-3 overflow-hidden"
    >
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-pb-text-muted">
          {whyLabel} · Root cause{" "}
          {rootIdentified ? "identified" : "not yet confirmed"}
        </div>
        <div className="flex items-center gap-2">
          {data.problem === undefined ? (
            <IconButton
              disabled={readOnly}
              onClick={addProblem}
              label="Add problem statement"
            >
              <Plus className="h-3.5 w-3.5" /> Add problem
            </IconButton>
          ) : null}
          <IconButton
            disabled={readOnly || data.steps.length >= maxSteps}
            onClick={() => insertWhy(data.steps.length)}
            label="Add Why"
          >
            <Plus className="h-3.5 w-3.5" /> Why
          </IconButton>
        </div>
      </div>

      <div className="flex min-h-56 flex-1 overflow-hidden rounded-lg border border-pb-border bg-pb-card-elevated/25 px-3">
        <CausalChainWidget
          data={data}
          onChange={() => undefined}
          readOnly={readOnly === true}
          fit
          selection={activeSelection}
          onSelect={setSelection}
        />
      </div>

      <div
        data-testid="causal-chain-inspector"
        className="shrink-0 rounded-lg border border-pb-border bg-pb-card-elevated/35 p-3"
      >
        {activeSelection?.kind === "problem" ? (
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <Field label="Problem statement">
              <textarea
                aria-label="Problem statement"
                rows={3}
                maxLength={255}
                disabled={readOnly}
                value={data.problem ?? ""}
                onChange={(event) =>
                  onChange({ ...data, problem: event.target.value })
                }
                className={cn(inputClass, "resize-none")}
              />
            </Field>
            <IconButton
              disabled={readOnly}
              onClick={removeProblem}
              label="Remove problem statement"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove problem
            </IconButton>
          </div>
        ) : selectedStep && activeSelection?.kind === "step" ? (
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label={`Why ${activeSelection.index + 1} title`}>
                <input
                  aria-label={`Why ${activeSelection.index + 1} title`}
                  maxLength={255}
                  disabled={readOnly}
                  value={selectedStep.label}
                  onChange={(event) =>
                    updateStep(activeSelection.index, {
                      label: event.target.value,
                    })
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Explanation">
                <textarea
                  aria-label={`Why ${activeSelection.index + 1} explanation`}
                  rows={3}
                  maxLength={500}
                  disabled={readOnly}
                  value={selectedStep.body}
                  onChange={(event) =>
                    updateStep(activeSelection.index, {
                      body: event.target.value,
                    })
                  }
                  className={cn(inputClass, "resize-none")}
                />
              </Field>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Switch
                checked={selectedStep.isRoot === true}
                disabled={readOnly}
                onChange={(checked) => setRoot(activeSelection.index, checked)}
                label="Root cause"
              />
              <div className="flex flex-wrap items-center gap-2">
                <IconButton
                  disabled={readOnly || data.steps.length >= maxSteps}
                  onClick={() => insertWhy(activeSelection.index)}
                  label={`Add Why before Why ${activeSelection.index + 1}`}
                >
                  <Plus className="h-3.5 w-3.5" /> Before
                </IconButton>
                <IconButton
                  disabled={readOnly || data.steps.length >= maxSteps}
                  onClick={() => insertWhy(activeSelection.index + 1)}
                  label={`Add Why after Why ${activeSelection.index + 1}`}
                >
                  <Plus className="h-3.5 w-3.5" /> After
                </IconButton>
                <IconButton
                  disabled={readOnly || activeSelection.index === 0}
                  onClick={() =>
                    moveStep(activeSelection.index, activeSelection.index - 1)
                  }
                  label={`Move Why ${activeSelection.index + 1} left`}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Move left
                </IconButton>
                <IconButton
                  disabled={
                    readOnly || activeSelection.index === data.steps.length - 1
                  }
                  onClick={() =>
                    moveStep(activeSelection.index, activeSelection.index + 1)
                  }
                  label={`Move Why ${activeSelection.index + 1} right`}
                >
                  <ArrowRight className="h-3.5 w-3.5" /> Move right
                </IconButton>
                <IconButton
                  disabled={readOnly}
                  onClick={() => removeStep(activeSelection.index)}
                  label={`Delete Why ${activeSelection.index + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </IconButton>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-20 items-center justify-center text-xs italic text-pb-text-muted">
            Add a problem statement or the first Why to begin.
          </div>
        )}
      </div>
    </div>
  );
}

function FishboneEditor({
  data,
  onChange,
  readOnly,
}: {
  data: FishboneParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState("");
  /*
   * PB-4923 AC5 — `effect`, every branch's `category`, and every cause's `text`
   * two collections down. The map form because the branches and their causes
   * are rendered in nested loops inside this one component. The new-category
   * draft below is not leased: it names no branch yet.
   */
  const lease = useFieldEditSessionMap();
  const knownCategories = [
    "Man",
    "Method",
    "Machine",
    "Material",
    "Measurement",
    "Environment",
  ].filter(
    (category) => !data.branches.some((branch) => branch.category === category),
  );
  const addCategory = (category: string): void => {
    if (readOnly) return;
    onChange({
      ...data,
      branches: [
        ...data.branches,
        {
          id: uid("branch"),
          category,
          causes: [{ id: uid("cause"), text: "New cause" }],
        },
      ],
    });
    setCategoryMenuOpen(false);
    setCategoryDraft("");
  };
  const updateBranch = (
    id: string,
    patch: Partial<FishboneParams["branches"][number]>,
  ): void => {
    onChange({
      ...data,
      branches: data.branches.map((b) =>
        b.id === id ? { ...b, ...patch } : b,
      ),
    });
  };
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="h-62 shrink-0 overflow-auto rounded-lg border border-pb-border bg-pb-card-elevated/30 p-2 space-y-2">
        <div className="grid grid-cols-[3rem_minmax(0,1fr)_auto] items-center gap-2">
          <span className="text-dense-1 uppercase tracking-wider font-semibold text-pb-text-muted">
            Effect
          </span>
          <input
            aria-label="Effect"
            value={data.effect}
            disabled={readOnly}
            {...lease(propTarget("effect"))}
            onChange={(event) =>
              onChange({ ...data, effect: event.target.value })
            }
            className={inputClass}
          />
          <div className="relative">
            <IconButton
              disabled={readOnly}
              onClick={() => setCategoryMenuOpen((open) => !open)}
              label="Add category"
              ariaExpanded={categoryMenuOpen}
            >
              <Plus className="w-3.5 h-3.5" /> Category
            </IconButton>
            {categoryMenuOpen ? (
              <div className="absolute end-0 top-full z-20 mt-1 flex w-64 flex-col gap-1.5 rounded-md border border-pb-border bg-pb-card p-2 shadow-xl">
                {knownCategories.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {knownCategories.map((category) => (
                      <button
                        key={category}
                        type="button"
                        disabled={readOnly}
                        onClick={() => addCategory(category)}
                        className="rounded-full border border-pb-control-border px-2 py-0.5 text-dense-1 text-pb-text hover:pb-overlay-paint"
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="flex items-center gap-1">
                  <input
                    aria-label="Custom category"
                    value={categoryDraft}
                    disabled={readOnly}
                    onChange={(event) => setCategoryDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && categoryDraft.trim()) {
                        addCategory(categoryDraft.trim());
                      }
                    }}
                    placeholder="Custom category"
                    className="min-w-0 flex-1 rounded border border-pb-control-border bg-pb-card-elevated px-2 py-1 text-xs focus-frame"
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={readOnly || !categoryDraft.trim()}
                    onClick={() => addCategory(categoryDraft.trim())}
                  >
                    Add
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div
          data-testid="fishbone-category-grid"
          className="grid grid-cols-3 gap-2"
        >
          {data.branches.map((branch, branchIndex) => (
            <section
              key={branch.id}
              className="group/cat rounded-md border border-pb-border bg-pb-card px-2 py-1.5"
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-full",
                    [
                      "bg-pb-cyan",
                      "bg-pb-amber",
                      "bg-pb-purple",
                      "bg-pb-teal",
                      "bg-pb-red",
                      "bg-pb-purple",
                    ][branchIndex % 6],
                  )}
                  aria-hidden
                />
                <input
                  aria-label={`${branch.category} category`}
                  value={branch.category}
                  disabled={readOnly}
                  {...lease(itemFieldTarget("branches", branch.id, "category"))}
                  onChange={(event) =>
                    updateBranch(branch.id, { category: event.target.value })
                  }
                  className="min-w-0 flex-1 rounded-md border-0 bg-transparent p-0 text-xs font-semibold text-pb-text focus-field"
                />
                <button
                  type="button"
                  aria-label={`Add cause to ${branch.category}`}
                  disabled={readOnly}
                  onClick={() =>
                    updateBranch(branch.id, {
                      causes: [
                        ...branch.causes,
                        { id: uid("cause"), text: "New cause" },
                      ],
                    })
                  }
                  className="rounded p-0.5 text-pb-text-muted hover:text-pb-text"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete category ${branchIndex + 1}: ${branch.category}`}
                  disabled={readOnly}
                  onClick={() =>
                    onChange({
                      ...data,
                      branches: data.branches.filter(
                        (entry) => entry.id !== branch.id,
                      ),
                    })
                  }
                  className="rounded p-0.5 text-pb-text-muted opacity-0 transition hover:bg-pb-red/10 hover:text-pb-red-text focus-ring group-hover/cat:opacity-100 group-focus-within/cat:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mt-1.5 space-y-1">
                {branch.causes.map((cause, index) => (
                  <div
                    key={cause.id}
                    className="group/cause flex items-center gap-1"
                  >
                    <input
                      aria-label={`${branch.category} cause ${index + 1}`}
                      value={cause.text}
                      disabled={readOnly}
                      {...lease(
                        nestedItemFieldTarget(
                          "branches",
                          branch.id,
                          "causes",
                          cause.id,
                          "text",
                        ),
                      )}
                      onChange={(event) =>
                        updateBranch(branch.id, {
                          causes: branch.causes.map((entry) =>
                            entry.id === cause.id
                              ? { ...entry, text: event.target.value }
                              : entry,
                          ),
                        })
                      }
                      className="min-w-0 flex-1 rounded bg-pb-card-elevated px-1.5 py-0.5 text-dense-1 text-pb-text-secondary focus-field"
                    />
                    <button
                      type="button"
                      aria-label={`Delete cause ${index + 1} from category ${branchIndex + 1} ${branch.category}: ${cause.text}`}
                      disabled={readOnly}
                      onClick={() =>
                        updateBranch(branch.id, {
                          causes: branch.causes.filter(
                            (entry) => entry.id !== cause.id,
                          ),
                        })
                      }
                      className="rounded p-0.5 text-pb-text-muted opacity-0 transition hover:bg-pb-red/10 hover:text-pb-red-text focus-ring group-hover/cause:opacity-100 group-focus-within/cause:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
      <div
        data-testid="fishbone-expanded-preview"
        className="flex-1 min-h-0 flex flex-col overflow-hidden"
      >
        <FishboneWidget
          data={data}
          onChange={() => undefined}
          readOnly
          compact={false}
          interactive
        />
      </div>
    </div>
  );
}

function impactEffortTier(
  impact: number,
  effort: number,
  scale: ImpactEffortScale,
  labels: Partial<Record<Tier, string>>,
): { label: string; badge: string; dot: string } {
  const tier = tierFor(impact, effort, scale);
  if (tier === "quick-win") {
    return {
      label: labels[tier] ?? "Quick Win",
      badge: "bg-pb-green/20 text-pb-green-text",
      dot: "bg-pb-green",
    };
  }
  if (tier === "major-projects") {
    return {
      label: labels[tier] ?? "Worth Planning",
      badge: "bg-pb-cyan/20 text-pb-cyan-text",
      dot: "bg-pb-cyan",
    };
  }
  if (tier === "fill-ins") {
    return {
      label: labels[tier] ?? "Consider",
      badge: "bg-pb-amber/20 text-pb-amber-text",
      dot: "bg-pb-amber",
    };
  }
  return {
    label: labels[tier] ?? "Avoid",
    badge: "bg-pb-red/20 text-pb-red-text",
    dot: "bg-pb-red",
  };
}

export interface ImpactEffortAddInput {
  readonly label: string;
  readonly impact: number;
  readonly effort: number;
}

export interface ImpactEffortParkingItem {
  readonly id: string;
  readonly label: string;
  /** Optional host-owned structured prefix/body; shared UI never parses label. */
  readonly leadingLabel?: string | undefined;
  readonly displayLabel?: string | undefined;
  readonly parked: boolean;
  /** A host-owned reason that prevents this row being parked or placed. The
   * shared editor exposes it contextually and never interprets it. */
  readonly parkingDisabledReason?: string | undefined;
  /** Canonical coordinates retained by the host while a point is parked or
   * while its visible projection is catching up after unpark. */
  readonly point?: ImpactEffortParams["points"][number] | undefined;
}

export interface ImpactEffortAdditionalTableColumn {
  readonly id: string;
  readonly header: ReactNode;
  /** Semantic layout only; consumers never duplicate width/alignment classes. */
  readonly kind?: "text" | "numeric" | "status" | "actions" | undefined;
  readonly render: (row: {
    readonly id: string;
    readonly label: string;
    readonly parked: boolean | undefined;
    readonly point: ImpactEffortParams["points"][number] | undefined;
  }) => ReactNode;
}

type ImpactEffortTableRow = {
  readonly id: string;
  readonly label: string;
  readonly leadingLabel?: string | undefined;
  readonly displayLabel?: string | undefined;
  readonly parked: boolean | undefined;
  readonly parkingDisabledReason?: string | undefined;
  readonly point: ImpactEffortParams["points"][number] | undefined;
  readonly sourcePoint: ImpactEffortParams["points"][number] | undefined;
};

type ImpactEffortTableColumnKind =
  | "name"
  | "quadrant"
  | "parking"
  | "numeric"
  | "actions"
  | "text"
  | "status"
  | "default";

type ImpactEffortTableColumn = {
  readonly id: string;
  readonly header: ReactNode;
  readonly kind: ImpactEffortTableColumnKind;
  readonly render: (row: ImpactEffortTableRow) => ReactNode;
};

/* Each kind's alignment and floor. The design-system DataTable splits the
   width equally between the columns (layout="fixed"); the actions column alone
   is a fixed width. The floors become the table's min-width, so the box
   scrolls sideways before a column shrinks below its floor. Every shared
   column takes the LARGEST floor, so no built-in floor goes above 136px: the
   five built-in columns plus actions need 48 + 5 × 136 = 728px, within the
   ~744px the weighted table needed, and fit the expanded panel. */
type ImpactEffortColumnLayout = {
  readonly align: "start" | "center" | "end";
  readonly minPx: number;
  /** A fixed width in px; the other columns share what is left equally. */
  readonly widthPx?: number;
};

const IMPACT_EFFORT_COLUMN_LAYOUT: Record<
  ImpactEffortTableColumnKind,
  ImpactEffortColumnLayout
> = {
  name: { align: "start", minPx: 136 },
  quadrant: { align: "start", minPx: 136 },
  parking: { align: "center", minPx: 136 },
  numeric: { align: "end", minPx: 96 },
  actions: { align: "center", minPx: 48, widthPx: 48 },
  text: { align: "start", minPx: 144 },
  status: { align: "center", minPx: 128 },
  default: { align: "start", minPx: 144 },
};

function impactEffortColumnLayout(
  kind: ImpactEffortTableColumnKind,
): ImpactEffortColumnLayout {
  return IMPACT_EFFORT_COLUMN_LAYOUT[kind];
}

export interface ImpactEffortExpandedEditorProps {
  readonly data: ImpactEffortParams;
  readonly onChange: (next: unknown) => void;
  readonly readOnly?: boolean | undefined;
  readonly onAdd?: ((input: ImpactEffortAddInput) => void) | undefined;
  /** Add an unscored item directly to the parking tray. Hosts own persistence;
   * the editor supplies only the text captured by the compact popover. */
  readonly onAddToParking?: ((label: string) => void) | undefined;
  readonly addPending?: boolean | undefined;
  readonly onRemove?: ((id: string) => void) | undefined;
  /** Host-owned point movement. Omit to retain the editor's onChange path. */
  readonly onMove?:
    | ((
        id: string,
        patch: Partial<ImpactEffortParams["points"][number]>,
      ) => void)
    | undefined;
  /**
   * Rename an existing item in place, from the table's Name cell (PB-4551).
   *
   * OPT-IN. Absent, the Name cell renders exactly as it always has — plain
   * text, delete the only row action — so Hoshin and VSM are untouched. It is
   * a separate prop rather than a branch of `onMove` because a host's move
   * handler persists COORDINATES: VSM's writes impact/effort through its own
   * artifact endpoint, and quietly widening it to carry a label would post a
   * rename through a path built for a drag.
   *
   * Name only, deliberately. Quadrant, impact and effort are already editable
   * — by dragging the point, or through the host's own cells — and the
   * complaint was that changing a NAME meant deleting the Kaizen and retyping
   * every one of them.
   */
  readonly onRenameItem?:
    | ((id: string, label: string) => void | Promise<void>)
    | undefined;
  /** Id currently being renamed remotely; its row stays disabled meanwhile. */
  readonly renamePendingId?: string | null | undefined;
  readonly canRemove?: boolean | undefined;
  readonly showRemoveWhenDisabled?: boolean | undefined;
  readonly completeTooltip?: boolean | undefined;
  readonly overlapMode?: ImpactEffortOverlapMode | undefined;
  readonly markerLabel?:
    | ((point: ImpactEffortParams["points"][number]) => string)
    | undefined;
  /** Optional host-owned parking projection. When supplied, the table shows
   * every item in this list even when it is not currently plotted. */
  readonly parkingItems?: readonly ImpactEffortParkingItem[] | undefined;
  readonly onParkingChange?:
    | ((id: string, parked: boolean) => void | Promise<void>)
    | undefined;
  /** Atomically unpark an item at the coordinates selected in the chart. Hosts
   * with remote persistence use this to order the parking and placement writes. */
  readonly onPlaceFromParking?:
    | ((
        id: string,
        scores: { readonly impact: number; readonly effort: number },
      ) => void | Promise<void>)
    | undefined;
  /** Id currently being persisted. All parking actions stay disabled while a
   * host mutation is in flight so rows cannot race one shared mutation state. */
  readonly parkingPendingId?: string | null | undefined;
  readonly parkingDisabled?: boolean | undefined;
  readonly additionalTableColumns?:
    | readonly ImpactEffortAdditionalTableColumn[]
    | undefined;
  readonly removeActionLabel?:
    | ((item: { readonly id: string; readonly label: string }) => string)
    | undefined;
  /** Controlled view mode for hosts that place the Chart/Table switch in their
   * own dialog header. Omit both props to retain the editor-owned switch. */
  readonly mode?: "chart" | "table" | undefined;
  readonly onModeChange?: ((mode: "chart" | "table") => void) | undefined;
  readonly showModeControls?: boolean | undefined;
  /** Default `parking` preserves Hoshin. `combined` puts unscored and parked
   * rows into the same Ideas to place tray without merging their state. */
  readonly trayPresentation?: "parking" | "combined" | undefined;
  readonly scale?: ImpactEffortScale | undefined;
  readonly quadrantLabels?: Partial<Record<Tier, string>> | undefined;
  readonly categoryLabels?: Partial<Record<Tier, string>> | undefined;
  /** Score increment used when a tray item is dropped. Defaults to the
   * editor's established whole-number placement behaviour. */
  readonly placementStep?: number | undefined;
  readonly renderImpactCell?:
    | ((row: ImpactEffortTableRow) => ReactNode)
    | undefined;
  readonly renderEffortCell?:
    | ((row: ImpactEffortTableRow) => ReactNode)
    | undefined;
  /** Host-owned final-column actions. Omit to keep the editor's remove action. */
  readonly renderActionCell?:
    | ((row: ImpactEffortTableRow) => ReactNode)
    | undefined;
  readonly showRemoveColumn?: boolean | undefined;
}

export function ImpactEffortExpandedEditor({
  data,
  onChange,
  readOnly,
  onAdd,
  onAddToParking,
  addPending = false,
  onRemove,
  onMove,
  onRenameItem,
  renamePendingId,
  canRemove = true,
  showRemoveWhenDisabled = false,
  completeTooltip = false,
  overlapMode,
  markerLabel,
  parkingItems,
  onParkingChange,
  onPlaceFromParking,
  parkingPendingId,
  parkingDisabled = false,
  additionalTableColumns = [],
  removeActionLabel,
  mode: controlledMode,
  onModeChange,
  showModeControls = true,
  trayPresentation = "parking",
  scale = { min: 0, max: 10, midpoint: 5, step: 0.1 },
  quadrantLabels = {},
  categoryLabels = {},
  placementStep = 1,
  renderImpactCell,
  renderEffortCell,
  renderActionCell,
  showRemoveColumn = true,
}: ImpactEffortExpandedEditorProps): ReactElement {
  const [localMode, setLocalMode] = useState<"chart" | "table">("chart");
  const mode = controlledMode ?? localMode;
  const setMode = (next: "chart" | "table"): void => {
    if (onModeChange) onModeChange(next);
    else setLocalMode(next);
  };
  const [addFormOpen, setAddFormOpen] = useState(false);
  const addPopoverHeadingId = useId();
  // PB-4551: which row is being renamed, and the in-progress text. One row at
  // a time — a table of simultaneously-open text fields has no clear Save.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  /*
   * PB-4923 AC5 — `points[id]/label` ONLY, and the omission is the point.
   *
   * `impact` and `effort` are mapped fields too, but the only way to change
   * them here is the chart DRAG, and how a drag should behave against a
   * concurrent edit to the SAME field is the open policy question (G1) — it is
   * not settled, so nothing is claimed for it. A rename is not that question:
   * it is a text field with a Save, identical to every other leaf in this file,
   * and leaving it unprotected because a neighbouring decision is open would
   * withhold protection nobody disputed. `field-edit-session-coverage.test.tsx`
   * pins BOTH halves — that `label` is claimed and that the coordinates are
   * not — so resolving G1 has to come back through this comment.
   */
  const lease = useFieldEditSessionMap();
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  const renameTriggerRefs = useRef(new Map<string, HTMLButtonElement>());
  /** The row whose Rename trigger is owed focus once its editor closes. */
  const restoreFocusTo = useRef<string | null>(null);
  // Explicit focus management for the inline rename — the ref + effect pattern
  // `ColumnHeaderPill` already uses. `autoFocus` is banned here
  // (jsx-a11y/no-autofocus) and in any case only ever solved half of this: it
  // moves focus INTO the field and says nothing about where focus lands when
  // the field unmounts on Save or Cancel, which is `<body>`, so a keyboard
  // user loses their place in the table. Focus returns to the row's own
  // Rename trigger instead — the same restore `PopoverMenuItem` makes.
  useEffect(() => {
    if (renamingId !== null) {
      restoreFocusTo.current = renamingId;
      renameInputRef.current?.focus();
      return;
    }
    const rowId = restoreFocusTo.current;
    if (rowId === null) return;
    // A host that renames asynchronously keeps the row disabled while its
    // write is in flight, and focusing a disabled button is a no-op — so the
    // restore is held rather than spent, and paid on the run of this effect
    // that clearing `renamePendingId` causes.
    if (renamePendingId === rowId) return;
    // Keyed on the row id, not the label, so the trigger is still found after
    // a rename has changed the name it is labelled with.
    const trigger = renameTriggerRefs.current.get(rowId);
    if (!trigger) return;
    trigger.focus();
    restoreFocusTo.current = null;
  }, [renamingId, renamePendingId]);
  const [addDraft, setAddDraft] = useState({
    label: "",
    impactPct: 70,
    effortPct: 30,
  });
  const [parkingSearch, setParkingSearch] = useState("");
  const [selectedParkingId, setSelectedParkingId] = useState<string | null>(
    null,
  );
  const [matrixDropOverParking, setMatrixDropOverParking] = useState(false);
  const [matrixParkingDropReason, setMatrixParkingDropReason] = useState<
    string | null
  >(null);
  const [parkingDrag, setParkingDrag] = useState<{
    readonly id: string;
    readonly label: string;
    readonly clientX: number;
    readonly clientY: number;
    readonly overMatrix: boolean;
  } | null>(null);
  const parkingPaneRef = useRef<HTMLDivElement | null>(null);
  const matrixRegionRef = useRef<HTMLDivElement | null>(null);
  const parkingDragCleanupRef = useRef<(() => void) | null>(null);
  // Set when a pointer gesture on a parked idea turned into a drag, so the
  // click that ends it on the same button does not toggle the selection.
  const parkingDragMovedRef = useRef(false);
  useEffect(() => () => parkingDragCleanupRef.current?.(), []);
  const parkingEnabled = parkingItems !== undefined;
  const pointById = new Map(data.points.map((point) => [point.id, point]));
  const parkingById = new Map<string, ImpactEffortParkingItem>();
  for (const item of parkingItems ?? []) parkingById.set(item.id, item);
  const tableRows: ImpactEffortTableRow[] = [];
  const tableRowIds = new Set<string>();
  for (const item of parkingById.values()) {
    const sourcePoint = pointById.get(item.id) ?? item.point;
    if (
      (!sourcePoint && !item.parked && trayPresentation === "parking") ||
      tableRowIds.has(item.id)
    )
      continue;
    tableRows.push({
      ...item,
      // The table always renders the canonical point snapshot. Parking affects
      // only the chart projection below; it must not blank or replace table
      // values while the host persistence projection catches up.
      point: sourcePoint,
      sourcePoint,
    });
    tableRowIds.add(item.id);
  }
  for (const point of data.points) {
    if (tableRowIds.has(point.id)) continue;
    tableRows.push({
      id: point.id,
      label: point.label,
      parked: parkingEnabled ? false : undefined,
      point,
      sourcePoint: point,
    });
    tableRowIds.add(point.id);
  }
  const chartData = {
    ...data,
    points: tableRows.flatMap((row) =>
      row.parked || !row.sourcePoint
        ? []
        : [{ ...row.sourcePoint, id: row.id, label: row.label }],
    ),
  };
  const parkedRows = tableRows.filter((row) => row.parked === true);
  const unscoredRows = tableRows.filter(
    (row) => row.parked !== true && row.sourcePoint === undefined,
  );
  const matchesParkingSearch = (row: ImpactEffortTableRow): boolean =>
    row.label
      .toLocaleLowerCase()
      .includes(parkingSearch.trim().toLocaleLowerCase());
  const filteredParkedRows = parkedRows.filter(matchesParkingSearch);
  const filteredUnscoredRows = unscoredRows.filter(matchesParkingSearch);
  const filteredTrayRows =
    trayPresentation === "combined"
      ? [...filteredUnscoredRows, ...filteredParkedRows]
      : filteredParkedRows;
  const placementDisabled =
    readOnly === true ||
    parkingDisabled ||
    parkingPendingId != null ||
    onPlaceFromParking === undefined;
  const containsClientPoint = (
    element: Element | null,
    clientX: number,
    clientY: number,
  ): boolean => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  };
  const plotElement = (): HTMLElement | null =>
    matrixRegionRef.current?.querySelector<HTMLElement>(
      '[data-testid="impact-effort-plot"]',
    ) ?? null;
  const scoresAtClientPoint = (
    clientX: number,
    clientY: number,
    label: string,
  ): { impact: number; effort: number } | null => {
    const plot = plotElement();
    if (!plot) return null;
    const rect = plot.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    if (!containsClientPoint(plot, clientX, clientY)) return null;
    const xPct = Math.max(
      0,
      Math.min(100, ((clientX - rect.left) / rect.width) * 100),
    );
    const displayYPct = Math.max(
      0,
      Math.min(100, ((clientY - rect.top) / rect.height) * 100),
    );
    const scoreYPct = impactEffortDisplayYPctToScoreYPct(
      displayYPct,
      rect.height,
      impactEffortPillSize(label).height,
    );
    const span = scale.max - scale.min;
    const snap = (value: number): number =>
      Math.max(
        scale.min,
        Math.min(scale.max, Math.round(value / placementStep) * placementStep),
      );
    return {
      effort: snap(
        scale.effortDirection === "ascending"
          ? scale.min + (xPct / 100) * span
          : scale.max - (xPct / 100) * span,
      ),
      impact: snap(scale.max - (scoreYPct / 100) * span),
    };
  };
  const placeParkedIdea = (
    id: string,
    clientX: number,
    clientY: number,
  ): boolean => {
    if (placementDisabled) return false;
    const row = tableRows.find((candidate) => candidate.id === id);
    const scores = scoresAtClientPoint(clientX, clientY, row?.label ?? "");
    if (!scores) return false;
    void onPlaceFromParking?.(id, scores);
    setSelectedParkingId(null);
    return true;
  };
  const placeParkedIdeaFromKeyboard = (row: ImpactEffortTableRow): boolean => {
    if (placementDisabled) return false;
    const scores = row.sourcePoint
      ? { impact: row.sourcePoint.impact, effort: row.sourcePoint.effort }
      : { impact: scale.midpoint, effort: scale.midpoint };
    void onPlaceFromParking?.(row.id, scores);
    setSelectedParkingId(null);
    return true;
  };
  const beginParkingDrag = (
    event: ReactPointerEvent<HTMLButtonElement>,
    row: ImpactEffortTableRow,
  ): void => {
    if (placementDisabled || event.button !== 0) return;
    parkingDragCleanupRef.current?.();
    // Selection belongs to the click alone, as in the VSM palette: selecting
    // here too made the click that ends the same gesture toggle it back off.
    parkingDragMovedRef.current = false;
    const pointerId = event.pointerId;
    const start = { x: event.clientX, y: event.clientY };
    let moved = false;
    const move = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== pointerId) return;
      if (
        Math.abs(pointerEvent.clientX - start.x) +
          Math.abs(pointerEvent.clientY - start.y) >
        4
      ) {
        moved = true;
        parkingDragMovedRef.current = true;
      }
      if (!moved) return;
      pointerEvent.preventDefault();
      setParkingDrag({
        id: row.id,
        label: row.label,
        clientX: pointerEvent.clientX,
        clientY: pointerEvent.clientY,
        overMatrix: containsClientPoint(
          plotElement(),
          pointerEvent.clientX,
          pointerEvent.clientY,
        ),
      });
    };
    const finish = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== pointerId) return;
      cleanup();
      if (moved) {
        placeParkedIdea(row.id, pointerEvent.clientX, pointerEvent.clientY);
      }
      setParkingDrag(null);
    };
    const cancel = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== pointerId) return;
      cleanup();
      setParkingDrag(null);
    };
    const cleanup = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      parkingDragCleanupRef.current = null;
    };
    parkingDragCleanupRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
  };
  const updatePoint = (
    id: string,
    patch: Partial<ImpactEffortParams["points"][number]>,
  ): void => {
    const sourcePoint = chartData.points.find((point) => point.id === id);
    if (!sourcePoint) return;
    if (onMove) {
      onMove(id, patch);
      return;
    }
    const existsInData = data.points.some((point) => point.id === id);
    onChange({
      ...data,
      points: existsInData
        ? data.points.map((point) =>
            point.id === id ? { ...point, ...patch } : point,
          )
        : [...data.points, { ...sourcePoint, ...patch }],
    });
  };
  const columns: ImpactEffortTableColumn[] = [
    {
      id: "name",
      header: "Name",
      kind: "name",
      render: (row) => {
        // Read-only, or a host that has not opted in: exactly what this cell
        // has always rendered.
        if (!onRenameItem || readOnly) {
          return (
            <span className="block truncate font-medium text-pb-text">
              {row.label}
            </span>
          );
        }
        const renamePending = renamePendingId === row.id;
        if (renamingId === row.id) {
          const trimmed = renameDraft.trim();
          // An empty name is not a rename, it is a deletion by other means —
          // and Delete is right there. Unchanged text is a no-op we simply
          // close on rather than posting.
          const canSave = trimmed.length > 0 && !renamePending;
          const submit = (): void => {
            if (!canSave) return;
            if (trimmed !== row.label) void onRenameItem(row.id, trimmed);
            setRenamingId(null);
          };
          return (
            <div className="flex items-center gap-1.5">
              <DsInput
                ref={renameInputRef}
                aria-label={`Rename ${row.label}`}
                value={renameDraft}
                disabled={renamePending}
                {...lease(itemFieldTarget("points", row.id, "label"))}
                onChange={(event) => setRenameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submit();
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    // STOP it here. This editor renders inside
                    // `ExpandedCardModal`, whose `useDismissable` listens on the
                    // DOCUMENT — `preventDefault` does not stop propagation, so
                    // one press cancelled the rename AND closed the panel the
                    // reader was renaming in. Escape now means "abandon this
                    // rename", and a second press closes the editor.
                    event.stopPropagation();
                    setRenamingId(null);
                  }
                }}
                className="h-7 py-0 text-xs"
              />
              <DsButton
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Save name for ${row.label}`}
                disabled={!canSave}
                onClick={submit}
              >
                <Check className="h-3.5 w-3.5" aria-hidden />
              </DsButton>
              <DsButton
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Cancel renaming ${row.label}`}
                disabled={renamePending}
                onClick={() => setRenamingId(null)}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </DsButton>
            </div>
          );
        }
        return (
          <div className="group/name flex items-center gap-1.5">
            <span className="min-w-0 flex-1 truncate font-medium text-pb-text">
              {row.label}
            </span>
            {/* Always in the DOM, revealed on hover/focus — a control that
                only EXISTS on hover is unreachable by keyboard and invisible
                on touch. */}
            <DsButton
              ref={(node) => {
                if (node) renameTriggerRefs.current.set(row.id, node);
                else renameTriggerRefs.current.delete(row.id);
              }}
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={`Rename ${row.label}`}
              disabled={renamePending}
              className="shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/name:opacity-100"
              onClick={() => {
                setRenameDraft(row.label);
                setRenamingId(row.id);
              }}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </DsButton>
          </div>
        );
      },
    },
    {
      id: "quadrant",
      header: "Quadrant",
      kind: "quadrant",
      render: (row) => {
        const tier = row.point
          ? impactEffortTier(
              row.point.impact,
              row.point.effort,
              scale,
              categoryLabels,
            )
          : undefined;
        return tier ? (
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-dense-1 font-medium",
              tier.badge,
            )}
          >
            <span
              className={cn("inline-block h-1.5 w-1.5 rounded-full", tier.dot)}
              aria-hidden
            />
            {tier.label}
          </span>
        ) : (
          <span className="text-pb-text-muted">—</span>
        );
      },
    },
    ...(parkingEnabled
      ? [
          {
            id: "parking",
            header: "Parking",
            kind: "parking" as const,
            render: (row: ImpactEffortTableRow) => {
              const parkingPending = parkingPendingId === row.id;
              const parkingAction = row.parked ? "Unpark" : "Park";
              const control = (
                <Switch
                  aria-label={
                    row.parkingDisabledReason
                      ? `${parkingAction} ${row.label}: ${row.parkingDisabledReason}`
                      : `${parkingAction} ${row.label}`
                  }
                  aria-busy={parkingPending || undefined}
                  checked={row.parked === true}
                  disabled={
                    readOnly ||
                    parkingDisabled ||
                    parkingPendingId != null ||
                    row.parkingDisabledReason !== undefined ||
                    onParkingChange === undefined
                  }
                  onChange={(parked) => {
                    void onParkingChange?.(row.id, parked);
                  }}
                  className="justify-center gap-2"
                  label={
                    <span className="min-w-20 text-start text-dense-1 font-medium text-pb-text-secondary">
                      {row.parked ? "Parked" : "Unparked"}
                    </span>
                  }
                />
              );
              return row.parkingDisabledReason ? (
                <Tooltip
                  content={row.parkingDisabledReason}
                  size="lg"
                  contentClassName="max-w-64"
                >
                  {control}
                </Tooltip>
              ) : (
                control
              );
            },
          },
        ]
      : []),
    ...additionalTableColumns.map((column) => ({
      id: column.id,
      header: column.header,
      kind: column.kind ?? ("default" as const),
      render: (row: ImpactEffortTableRow) => column.render(row),
    })),
    {
      id: "impact",
      header: "Impact",
      kind: "numeric",
      render: (row) =>
        renderImpactCell?.(row) ??
        (row.point ? `${Math.round(row.point.impact * 10)}%` : "—"),
    },
    {
      id: "effort",
      header: "Effort",
      kind: "numeric",
      render: (row) =>
        renderEffortCell?.(row) ??
        (row.point ? `${Math.round(row.point.effort * 10)}%` : "—"),
    },
    ...(showRemoveColumn || renderActionCell
      ? [
          {
            id: "actions",
            header: <span className="sr-only">Actions</span>,
            kind: "actions" as const,
            render: (row: ImpactEffortTableRow) =>
              renderActionCell ? (
                renderActionCell(row)
              ) : row.sourcePoint || onRemove ? (
                <button
                  type="button"
                  aria-label={removeActionLabel?.(row) ?? `Delete ${row.label}`}
                  disabled={readOnly || !canRemove}
                  onClick={() => {
                    if (onRemove) {
                      onRemove(row.id);
                    } else {
                      const next: ImpactEffortParams = {
                        ...data,
                        points: data.points.filter(
                          (entry) => entry.id !== row.id,
                        ),
                      };
                      if (data.excludedIds !== undefined) {
                        next.excludedIds = data.excludedIds.filter(
                          (id) => id !== row.id,
                        );
                      }
                      onChange(next);
                    }
                  }}
                  className="rounded p-1 text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ) : null,
          },
        ]
      : []),
  ];
  const tableColumns: DataTableColumn<ImpactEffortTableRow>[] = columns.map(
    (column) => {
      const layout = impactEffortColumnLayout(column.kind);
      return {
        id: column.id,
        header: column.header,
        align: layout.align,
        minWidth: layout.minPx,
        ...(layout.widthPx !== undefined ? { width: layout.widthPx } : {}),
        cell: column.render,
      };
    },
  );
  const addIdea = (): void => {
    const label = addDraft.label.trim();
    if (!label || readOnly || addPending) return;
    const input = {
      label,
      impact: addDraft.impactPct / 10,
      effort: addDraft.effortPct / 10,
    };
    if (onAdd) {
      onAdd(input);
    } else {
      onChange({
        ...data,
        points: [...data.points, { id: uid("idea"), ...input }],
      });
    }
    setAddDraft({ label: "", impactPct: 70, effortPct: 30 });
    setAddFormOpen(false);
  };
  const cancelAdd = (): void => {
    setAddDraft({ label: "", impactPct: 70, effortPct: 30 });
    setAddFormOpen(false);
  };
  const addIdeaToParking = (): void => {
    const label = addDraft.label.trim();
    if (!label || readOnly || addPending || !onAddToParking) return;
    onAddToParking(label);
    setAddDraft({ label: "", impactPct: 70, effortPct: 30 });
    setAddFormOpen(false);
  };
  return (
    <div className="@container/impact-effort flex-1 min-h-0 min-w-0 flex flex-col gap-3">
      {showModeControls ? (
        <div className="z-20 flex shrink-0 items-center justify-center gap-2 @min-[42rem]/impact-effort:absolute @min-[42rem]/impact-effort:top-3 @min-[42rem]/impact-effort:start-1/2 @min-[42rem]/impact-effort:-translate-x-1/2 @min-[42rem]/impact-effort:rtl:translate-x-1/2">
          <div className="inline-flex rounded-lg bg-pb-card-elevated p-1">
            <button
              type="button"
              aria-label="Chart"
              aria-pressed={mode === "chart"}
              onClick={() => setMode("chart")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium",
                mode === "chart"
                  ? "bg-pb-card text-pb-text shadow-sm"
                  : "text-pb-text-muted",
              )}
            >
              <ChartNoAxesCombined className="h-3.5 w-3.5" /> Chart
            </button>
            <button
              type="button"
              aria-label="Table"
              aria-pressed={mode === "table"}
              onClick={() => setMode("table")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium",
                mode === "table"
                  ? "bg-pb-card text-pb-text shadow-sm"
                  : "text-pb-text-muted",
              )}
            >
              <Table2 className="h-3.5 w-3.5" /> Table
            </button>
          </div>
          {!parkingEnabled ? (
            <button
              type="button"
              aria-label="Add Kaizen"
              disabled={readOnly || addPending || addFormOpen}
              onClick={() => setAddFormOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-pb-green/40 bg-pb-green/10 px-3 py-2 text-xs font-medium text-pb-green-text disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> Add Kaizen
            </button>
          ) : null}
        </div>
      ) : null}
      {addFormOpen && !parkingEnabled ? (
        <div className="mt-10 shrink-0 rounded-lg border border-pb-border bg-pb-card-elevated/60 p-3 flex flex-col gap-3">
          <input
            aria-label="Kaizen idea name"
            disabled={readOnly || addPending}
            value={addDraft.label}
            onChange={(event) =>
              setAddDraft((draft) => ({
                ...draft,
                label: event.target.value,
              }))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") addIdea();
              if (event.key === "Escape") cancelAdd();
            }}
            placeholder="Kaizen idea name…"
            className="w-full rounded-md border border-pb-control-border bg-pb-card px-3 py-1.5 text-xs text-pb-text placeholder:text-pb-text-muted focus-frame"
          />
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1 text-dense-1 font-semibold uppercase tracking-wide text-pb-text-secondary">
              Impact — {addDraft.impactPct}%
              <input
                aria-label="Impact percentage"
                disabled={readOnly || addPending}
                type="range"
                min={0}
                max={100}
                value={addDraft.impactPct}
                onChange={(event) =>
                  setAddDraft((draft) => ({
                    ...draft,
                    impactPct: Number(event.target.value),
                  }))
                }
                className="w-full accent-pb-green"
              />
            </label>
            <label className="flex flex-col gap-1 text-dense-1 font-semibold uppercase tracking-wide text-pb-text-secondary">
              Effort — {addDraft.effortPct}%
              <input
                aria-label="Effort percentage"
                disabled={readOnly || addPending}
                type="range"
                min={0}
                max={100}
                value={addDraft.effortPct}
                onChange={(event) =>
                  setAddDraft((draft) => ({
                    ...draft,
                    effortPct: Number(event.target.value),
                  }))
                }
                className="w-full accent-pb-cyan"
              />
            </label>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={cancelAdd}
              className="rounded-md px-3 py-1 text-xs text-pb-text-secondary transition-colors hover:pb-overlay-paint hover:text-pb-text"
            >
              Cancel
            </button>
            <button
              type="button"
              aria-label="Confirm Add Kaizen"
              disabled={readOnly || addPending || !addDraft.label.trim()}
              onClick={addIdea}
              className="rounded-md bg-pb-green px-3 py-1 text-xs font-medium text-pb-on-accent transition-colors hover:bg-pb-green/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Add Kaizen
            </button>
          </div>
        </div>
      ) : null}
      {mode === "chart" ? (
        <>
          <div
            data-testid="impact-effort-expanded-preview"
            className={cn(
              "flex-1 min-h-0 grid overflow-hidden rounded-lg border border-pb-border bg-pb-card",
              parkingEnabled
                ? "grid-cols-1 grid-rows-[minmax(12rem,1fr)_minmax(18rem,2fr)] overflow-y-auto @min-[42rem]/impact-effort:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)] @min-[42rem]/impact-effort:grid-rows-1 @min-[42rem]/impact-effort:overflow-hidden"
                : "grid-cols-1",
            )}
          >
            {parkingEnabled ? (
              <div
                ref={parkingPaneRef}
                data-testid="impact-effort-parking-pane"
                data-drop-active={matrixDropOverParking || undefined}
                className={cn(
                  "relative flex min-h-0 min-w-0 flex-col border-b border-pb-border bg-pb-card-elevated/30 p-4 transition-colors @min-[42rem]/impact-effort:border-b-0 @min-[42rem]/impact-effort:border-e",
                  matrixDropOverParking &&
                    "bg-pb-cyan/10 ring-2 ring-inset ring-pb-cyan/60",
                )}
              >
                <div className="shrink-0">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-pb-text">
                      Ideas to place
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-dense-1 font-medium text-pb-text-muted">
                        {parkedRows.length + unscoredRows.length} remaining
                      </span>
                      {onAddToParking ? (
                        <Popover
                          align="end"
                          contentRole="dialog"
                          open={addFormOpen}
                          onOpenChange={(open) => {
                            setAddFormOpen(open);
                            if (!open) {
                              setAddDraft({
                                label: "",
                                impactPct: 70,
                                effortPct: 30,
                              });
                            }
                          }}
                        >
                          <PopoverTrigger>
                            <DsButton
                              variant="ghost"
                              size="sm"
                              iconOnly
                              aria-label="Add Kaizen to parking"
                              disabled={readOnly || addPending}
                            >
                              <Plus className="h-4 w-4" aria-hidden />
                            </DsButton>
                          </PopoverTrigger>
                          <PopoverContent
                            padding="md"
                            className="w-80"
                            aria-labelledby={addPopoverHeadingId}
                          >
                            <div className="flex flex-col gap-3">
                              <div
                                id={addPopoverHeadingId}
                                className="text-sm font-semibold text-pb-text"
                              >
                                Add Kaizen
                              </div>
                              <DsTextarea
                                label="Kaizen idea"
                                placeholder="Describe the Kaizen idea…"
                                value={addDraft.label}
                                disabled={readOnly || addPending}
                                maxLength={2000}
                                showCount
                                onChange={(event) =>
                                  setAddDraft((draft) => ({
                                    ...draft,
                                    label: event.target.value,
                                  }))
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Escape") cancelAdd();
                                }}
                              />
                              <div className="flex justify-end gap-2">
                                <DsButton
                                  variant="ghost"
                                  size="sm"
                                  onClick={cancelAdd}
                                >
                                  Cancel
                                </DsButton>
                                <DsButton
                                  variant="primary"
                                  size="sm"
                                  aria-label="Save Kaizen"
                                  loading={addPending}
                                  disabled={readOnly || !addDraft.label.trim()}
                                  onClick={addIdeaToParking}
                                >
                                  Save
                                </DsButton>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : null}
                    </div>
                  </div>
                  <p className="mt-1 text-dense-1 text-pb-text-muted">
                    Drag an item onto the matrix or select it, then choose a
                    position.
                  </p>
                  {matrixParkingDropReason ? (
                    <div
                      role="status"
                      className="mt-2 rounded-md border border-pb-amber/40 bg-pb-amber/10 px-2.5 py-2 text-xs font-medium text-pb-amber-text"
                    >
                      {matrixParkingDropReason}
                    </div>
                  ) : null}
                  <DsInput
                    aria-label="Search parked ideas"
                    value={parkingSearch}
                    onChange={(event) => setParkingSearch(event.target.value)}
                    placeholder="Search ideas"
                    prefix={<Search className="h-3.5 w-3.5" aria-hidden />}
                    className="text-xs"
                    disabled={
                      parkedRows.length === 0 && unscoredRows.length === 0
                    }
                  />
                </div>
                <div className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto pe-1">
                  {[{ id: "parking", rows: filteredTrayRows }].map((group) => (
                    <section key={group.id} className="space-y-2">
                      {group.rows.map((row) => {
                        const selected = selectedParkingId === row.id;
                        return (
                          <DsButton
                            key={row.id}
                            variant="outline"
                            size="md"
                            aria-pressed={selected}
                            aria-label={
                              row.parkingDisabledReason !== undefined
                                ? `${row.label}: ${row.parkingDisabledReason}`
                                : selected
                                  ? `Place ${row.label} at its suggested position`
                                  : `Select ${row.label} to place`
                            }
                            disabled={
                              placementDisabled ||
                              row.parkingDisabledReason !== undefined
                            }
                            onClick={(event) => {
                              // A keyboard click (detail 0) never follows a drag.
                              if (
                                parkingDragMovedRef.current &&
                                event.detail !== 0
                              ) {
                                parkingDragMovedRef.current = false;
                                return;
                              }
                              setSelectedParkingId((current) =>
                                current === row.id ? null : row.id,
                              );
                            }}
                            onKeyDown={(event) => {
                              if (
                                selected &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                placeParkedIdeaFromKeyboard(row);
                              }
                            }}
                            onPointerDown={(event) =>
                              beginParkingDrag(event, row)
                            }
                            className={cn(
                              "h-auto min-h-11 w-full touch-none justify-start gap-2 px-2.5 py-2 text-start",
                              !placementDisabled &&
                                "cursor-grab active:cursor-grabbing",
                              selected &&
                                "border-pb-teal/40 bg-pb-teal/10 shadow-glow-selected",
                            )}
                          >
                            <GripVertical
                              className="h-4 w-4 shrink-0 text-pb-text-muted"
                              aria-hidden
                            />
                            {row.leadingLabel ? (
                              <span className="shrink-0 rounded bg-pb-card-elevated px-1.5 py-0.5 text-dense-1 font-semibold text-pb-text-secondary">
                                {row.leadingLabel}
                              </span>
                            ) : null}
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-medium text-pb-text">
                                {row.displayLabel ?? row.label}
                              </span>
                              {row.parkingDisabledReason !== undefined ? (
                                <span className="mt-0.5 block text-dense-1 text-pb-text-muted">
                                  {row.parkingDisabledReason}
                                </span>
                              ) : null}
                            </span>
                          </DsButton>
                        );
                      })}
                    </section>
                  ))}
                  {parkedRows.length === 0 && unscoredRows.length === 0 ? (
                    <div className="flex min-h-36 items-center justify-center rounded-lg border border-dashed border-pb-border px-5 text-center text-xs text-pb-text-muted">
                      All ideas are on the matrix. Drag one here to park it.
                    </div>
                  ) : filteredTrayRows.length === 0 ? (
                    <div className="flex min-h-28 items-center justify-center px-5 text-center text-xs text-pb-text-muted">
                      {trayPresentation === "parking"
                        ? "No parked ideas match your search."
                        : "No ideas match your search."}
                    </div>
                  ) : null}
                </div>
                {/* Kept hand-built (design-system adoption): the drop-target
                    overlay a dragged matrix point shows over the parking
                    tray — a drag-and-drop mark, not a notice or a card. */}
                {matrixDropOverParking ? (
                  <div
                    className={cn(
                      "pointer-events-none absolute inset-3 z-20 grid place-items-center rounded-lg border-2 border-dashed bg-pb-card/90 px-6 text-center text-sm font-semibold shadow-sm",
                      matrixParkingDropReason
                        ? "border-pb-amber text-pb-amber-text"
                        : "border-pb-cyan text-pb-cyan-text",
                    )}
                  >
                    {matrixParkingDropReason ?? "Drop to park"}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div
              ref={matrixRegionRef}
              data-testid="impact-effort-matrix-region"
              data-drop-active={parkingDrag?.overMatrix || undefined}
              className={cn(
                "flex min-h-0 min-w-0 flex-col p-3 transition-colors",
                parkingDrag?.overMatrix && "bg-pb-cyan/5",
              )}
              onPointerDown={(event) => {
                if (event.button !== 0) return;
                if (!selectedParkingId || parkingDrag) return;
                const target = event.target as HTMLElement;
                if (
                  target.closest(
                    '[data-testid="impact-effort-pill"], [data-testid="impact-effort-group"]',
                  )
                ) {
                  return;
                }
                placeParkedIdea(
                  selectedParkingId,
                  event.clientX,
                  event.clientY,
                );
              }}
            >
              <div className="mb-2 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-dense-1 text-pb-text-muted">
                <LegendDot tone="healthy" label="Quick Win" />
                <LegendDot tone="info" label="Worth Planning" />
                <LegendDot tone="watch" label="Consider" />
                <LegendDot tone="critical" label="Avoid" />
              </div>
              <div className="flex min-h-0 flex-1">
                <ImpactEffortWidget
                  data={chartData}
                  onChange={() => undefined}
                  compact={false}
                  readOnly={readOnly === true}
                  onMove={readOnly ? undefined : updatePoint}
                  onRemove={
                    readOnly
                      ? undefined
                      : (onRemove ??
                        ((id) => {
                          const next: ImpactEffortParams = {
                            ...data,
                            points: data.points.filter(
                              (point) => point.id !== id,
                            ),
                          };
                          if (data.excludedIds !== undefined) {
                            next.excludedIds = data.excludedIds.filter(
                              (excludedId) => excludedId !== id,
                            );
                          }
                          onChange(next);
                        }))
                  }
                  canRemove={canRemove}
                  showRemoveWhenDisabled={showRemoveWhenDisabled}
                  completeTooltip={completeTooltip}
                  overlapMode={overlapMode}
                  markerLabel={markerLabel}
                  scale={scale}
                  quadrantLabels={quadrantLabels}
                  categoryLabels={categoryLabels}
                  removeActionLabel={removeActionLabel}
                  onPointDragMove={({ id, clientX, clientY }) => {
                    const overParking = containsClientPoint(
                      parkingPaneRef.current,
                      clientX,
                      clientY,
                    );
                    setMatrixDropOverParking(overParking);
                    setMatrixParkingDropReason(
                      overParking
                        ? (parkingById.get(id)?.parkingDisabledReason ?? null)
                        : null,
                    );
                  }}
                  onPointDrop={({ id, clientX, clientY }) => {
                    const droppedOnParking = containsClientPoint(
                      parkingPaneRef.current,
                      clientX,
                      clientY,
                    );
                    setMatrixDropOverParking(false);
                    const disabledReason =
                      parkingById.get(id)?.parkingDisabledReason;
                    if (droppedOnParking && disabledReason !== undefined) {
                      setMatrixParkingDropReason(disabledReason);
                      return true;
                    }
                    if (
                      !droppedOnParking ||
                      readOnly ||
                      parkingDisabled ||
                      parkingPendingId != null ||
                      onParkingChange === undefined
                    ) {
                      return false;
                    }
                    setMatrixParkingDropReason(null);
                    void onParkingChange(id, true);
                    setSelectedParkingId(null);
                    return true;
                  }}
                  onPointDragCancel={() => {
                    setMatrixDropOverParking(false);
                    setMatrixParkingDropReason(null);
                  }}
                />
              </div>
            </div>
            {/* Kept hand-built (design-system adoption): the drag ghost that
                follows the pointer out of the parking tray — a drag-and-drop
                mark placed at the pointer, not a tooltip or a card. */}
            {parkingDrag ? (
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none fixed z-tooltip max-w-72 -translate-x-4 -translate-y-1/2 rounded-md border bg-pb-card px-3 py-2 text-xs font-medium text-pb-text shadow-lg",
                  parkingDrag.overMatrix
                    ? "border-pb-cyan ring-2 ring-pb-cyan/30"
                    : "border-pb-border",
                )}
                style={{
                  left: parkingDrag.clientX,
                  top: parkingDrag.clientY,
                }}
              >
                {parkingDrag.label}
              </div>
            ) : null}
          </div>
          <div className="text-dense-1 text-pb-text-muted shrink-0">
            Drag a point to update impact and effort · Drag parked ideas onto
            the matrix and placed ideas back to the left · Use Table for exact
            values
          </div>
        </>
      ) : (
        <div
          className={cn(
            "flex-1 min-h-0 overflow-auto",
            !addFormOpen && "mt-10",
          )}
        >
          {/* Layout: `h-full` on the DataTable and its framed box lets the
              frame fill the panel as the hand-built table's did. `striped`
              is the hand-built table's zebra, kept for this grid. */}
          <DsDataTable
            data-testid="impact-effort-table"
            caption="Impact and effort items"
            className="h-full [&>div]:h-full"
            layout="fixed"
            textSize="small"
            striped
            columns={tableColumns}
            data={tableRows}
            getRowId={(row) => row.id}
            emptyState={<EmptyState title="No items yet." />}
          />
        </div>
      )}
    </div>
  );
}

function LegendDot({
  tone,
  label,
}: {
  tone: StatusTone;
  label: string;
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot tone={tone} size="md" />
      {label}
    </span>
  );
}

function CountermeasureEditor({
  data,
  onChange,
  readOnly,
}: {
  data: CountermeasureTableParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  return (
    <CountermeasureExpandedEditor
      data={data}
      onChange={onChange}
      readOnly={readOnly ?? false}
    />
  );
}

function StakeholderMapEditor({
  data,
  onChange,
  readOnly,
}: {
  data: StakeholderMapParams;
  onChange: (
    next: unknown,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  readOnly?: boolean | undefined;
}): ReactElement {
  const [showAddForm, setShowAddForm] = useState(false);
  const labels: Record<
    StakeholderMapParams["stakeholders"][number]["quadrant"],
    string
  > = {
    manage: "Manage Closely",
    satisfy: "Keep Satisfied",
    inform: "Keep Informed",
    monitor: "Monitor",
  };
  const addStakeholder = (draft: StakeholderDraft): void | Promise<void> => {
    const name = draft.name.trim();
    if (readOnly || !name) return;
    const role = draft.role.trim();
    // A pick may add the person as Viewer, and the form waits on it (d25).
    const options: WidgetChangeOptions | undefined = draft.userId
      ? { grantViewerIfMissing: true }
      : undefined;
    const next = {
      ...data,
      stakeholders: [
        ...data.stakeholders,
        {
          id: uid("stake"),
          name,
          ...(draft.userId ? { userId: draft.userId } : {}),
          quadrant: draft.quadrant,
          ...(role ? { role } : {}),
        },
      ],
    };
    const saved = options ? onChange(next, options) : onChange(next);
    if (options) {
      return Promise.resolve(saved).then(() => setShowAddForm(false));
    }
    setShowAddForm(false);
  };
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div
        data-testid="stakeholder-expanded-layout"
        className="grid flex-1 min-h-0 grid-cols-5 gap-4"
      >
        <div className="col-span-3 min-h-0 flex flex-col">
          <StakeholderMapWidget
            data={data}
            onChange={(next, options) =>
              options ? onChange(next, options) : onChange(next)
            }
            {...(readOnly === undefined ? {} : { readOnly })}
            compact={false}
          />
          <div className="mt-2 text-dense-1 text-pb-text-muted">
            Drag to reposition · release snaps to the dropped quadrant · click a
            chip to edit
          </div>
        </div>
        <div className="col-span-2 min-h-0 overflow-auto">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-pb-text-muted">
              Stakeholders ({data.stakeholders.length})
            </h3>
            <IconButton
              disabled={readOnly}
              onClick={() => setShowAddForm(true)}
              label="Add stakeholder"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </IconButton>
          </div>
          {showAddForm ? (
            <div className="mb-3">
              <StakeholderAddForm
                variant="panel"
                readOnly={readOnly}
                onAdd={addStakeholder}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          ) : null}
          {data.stakeholders.length === 0 ? (
            <div className="rounded-md border border-dashed border-pb-border px-3 py-6 text-center text-xs italic text-pb-text-muted">
              No stakeholders yet · click Add to drop one onto the grid, then
              click the chip to name it.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {data.stakeholders.map((stakeholder) => (
                <li
                  key={stakeholder.id}
                  data-testid="stakeholder-detail-card"
                  className="flex items-center gap-2 rounded-md border border-pb-border bg-pb-card-elevated/40 px-2 py-1.5"
                >
                  <span
                    className={`inline-flex rounded-full border px-1.5 py-0.5 text-dense-1 font-semibold ${
                      stakeholder.quadrant === "manage"
                        ? "border-pb-red/40 bg-pb-red/15 text-pb-red-text"
                        : stakeholder.quadrant === "satisfy"
                          ? "border-pb-amber/40 bg-pb-amber/15 text-pb-amber-text"
                          : stakeholder.quadrant === "inform"
                            ? "border-pb-teal/40 bg-pb-teal/15 text-pb-teal-text"
                            : "border-pb-border bg-pb-card-elevated text-pb-text-muted"
                    }`}
                  >
                    {labels[stakeholder.quadrant]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-pb-text">
                      {stakeholder.name}
                    </div>
                    {stakeholder.role ? (
                      <div className="truncate text-dense-1 text-pb-text-muted">
                        {stakeholder.role}
                      </div>
                    ) : null}
                  </div>
                  <TrashButton
                    disabled={readOnly}
                    label={`Delete ${stakeholder.name}`}
                    onClick={() =>
                      onChange({
                        ...data,
                        stakeholders: data.stakeholders.filter(
                          (entry) => entry.id !== stakeholder.id,
                        ),
                      })
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ControlPlanEditor({
  data,
  onChange,
  readOnly,
}: {
  data: ControlPlanParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  return (
    <ControlPlanExpandedEditor
      data={data}
      onChange={onChange}
      readOnly={readOnly ?? false}
    />
  );
}

function EscalationMatrixEditor(props: {
  data: EscalationMatrixParams;
  onChange: (next: unknown) => void;
  readOnly?: boolean | undefined;
}): ReactElement {
  return (
    <EscalationMatrixExpandedEditor {...props} readOnly={!!props.readOnly} />
  );
}

function ListEditor<T extends { id: string; text: string }>({
  title,
  addLabel,
  values,
  placeholderPrefix,
  onChange,
  disabled,
  collection,
}: {
  title: string;
  addLabel: string;
  values: readonly T[];
  placeholderPrefix: string;
  disabled?: boolean | undefined;
  onChange: (next: T[]) => void;
  /**
   * PB-4923 AC5 — the params collection these rows live in, so each row can
   * lease its own target. Optional: a caller that renders a list which is not
   * an addressable collection leases nothing, which is the correct answer
   * rather than a guessed key.
   */
  collection?: string | undefined;
}): ReactElement {
  return (
    <Panel
      title={title}
      action={
        <IconButton
          disabled={disabled}
          onClick={() =>
            onChange([
              ...values,
              {
                id: uid(placeholderPrefix.toLowerCase()),
                text: `${placeholderPrefix} ${values.length + 1}`,
              } as T,
            ])
          }
          label={addLabel}
        >
          <Plus className="w-3.5 h-3.5" /> {addLabel}
        </IconButton>
      }
    >
      <div className="space-y-2">
        {values.map((item, index) => (
          <div key={item.id} className="flex gap-2">
            <TextInput
              value={item.text}
              onChange={(v) =>
                onChange(
                  values.map((it, i) =>
                    i === index ? { ...it, text: v } : it,
                  ) as T[],
                )
              }
              disabled={disabled}
              {...(collection === undefined
                ? {}
                : { targetKey: itemFieldTarget(collection, item.id, "text") })}
            />
            <TrashButton
              disabled={disabled}
              onClick={() =>
                onChange(values.filter((_, i) => i !== index) as T[])
              }
            />
          </div>
        ))}
      </div>
    </Panel>
  );
}

function EditorShell({
  children,
  actions,
  readOnly,
}: {
  children: React.ReactNode;
  actions?: React.ReactNode;
  readOnly?: boolean | undefined;
}): ReactElement {
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-pb-text-muted">
          {readOnly ? "Read-only" : "Changes save automatically"}
        </div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto pe-1 space-y-4">
        {children}
      </div>
    </div>
  );
}

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): ReactElement {
  return (
    <section className="rounded-lg border border-pb-border bg-pb-card-elevated/30 p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold text-pb-text uppercase tracking-wider">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

const fieldLabelClass =
  "text-dense-1 uppercase tracking-wider font-semibold text-pb-text-muted";

function Field({
  label,
  children,
  htmlFor,
}: {
  label: string;
  children: React.ReactNode;
  /**
   * PB-1559: pass this (paired with the same `id` on the control) when a child
   * renders anything the label must not absorb — a validation message, say.
   * The default wraps `children` in the <label>, so an implicitly-labelled
   * control is named by the whole subtree and a message inside it joins the
   * accessible name: "Target value Target must be a number.", announced twice.
   * An explicit htmlFor keeps the message a sibling of the label instead.
   */
  htmlFor?: string | undefined;
}): ReactElement {
  if (htmlFor !== undefined) {
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={htmlFor} className={fieldLabelClass}>
          {label}
        </label>
        {children}
      </div>
    );
  }
  return (
    <label className="flex flex-col gap-1.5">
      <span className={fieldLabelClass}>{label}</span>
      {children}
    </label>
  );
}

function DataTable({
  headers,
  children,
}: {
  headers: readonly string[];
  children: React.ReactNode;
}): ReactElement {
  return (
    <div className="overflow-auto rounded-lg border border-pb-border">
      <table className="w-full min-w-0 table-fixed text-start text-xs">
        <thead className="bg-pb-card-elevated text-pb-text-muted uppercase tracking-wider text-dense-2">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-2 py-2 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-pb-border">{children}</tbody>
      </table>
    </div>
  );
}

function TextInput({
  value,
  onChange,
  disabled,
  targetKey,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean | undefined;
  /**
   * PB-4923 AC5 — the canonical merge-register address of the field this input
   * edits, so focusing it claims an editing session. Omitted where the editor
   * has not been wired yet; the control then leases nothing rather than
   * claiming a target it cannot name.
   */
  targetKey?: string | undefined;
}): ReactElement {
  const lease = useFieldEditSession(targetKey);
  return (
    <input
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onFocus={lease.onFocus}
      onBlur={lease.onBlur}
      className={inputClass}
    />
  );
}

/**
 * Text field for values the model stores in a *parsed* form (a number, or a
 * comma-separated number list). Binding an input straight to the re-serialised
 * model (`String(value)` / `recentValues.join(", ")`) snaps the controlled
 * value back mid-edit — a trailing "." / "-" / "," is dropped and the caret
 * jumps, so the field feels sticky and a number can't be cleared to retype it.
 *
 * This holds a local draft string as the source of truth *while focused*, so
 * in-progress text survives each keystroke; it still reports every change to
 * `onChange` (the caller parses `raw`), and only re-seeds from the canonical
 * `value` when not actively editing (an external update) or on blur (to
 * normalise, e.g. drop a trailing comma).
 */
type DraftFieldProps = {
  value: string;
  onChange: (raw: string) => void;
  multiline?: boolean | undefined;
  disabled?: boolean | undefined;
  /** PB-4923 AC5 — see `TextInput.targetKey`. */
  targetKey?: string | undefined;
} & (
  | { validate?: undefined; id?: string | undefined }
  | {
      /**
       * PB-1559: describes a draft the caller will not commit. The caller parses
       * `raw` and silently keeps the prior value when it cannot (see
       * `setTarget`), which on its own leaves the text vanishing on blur with
       * nothing said. This renders the returned message and marks the field
       * invalid, so the reason is on screen while it is still being typed.
       * Return null when the draft is committable.
       */
      validate: (raw: string) => string | null;
      /**
       * Required alongside `validate` — hence the union rather than two optional
       * props — and must match the `htmlFor` on the enclosing `Field`. Without
       * the explicit pairing `Field` wraps the control in its <label>, the
       * message renders inside that subtree, and it is absorbed into the
       * control's accessible name.
       */
      id: string;
    }
);

/**
 * Kept (currently unwired) for the same reason as `validateTarget` above: its
 * two consumers were the KPI Tile's target and recent-values fields, and KPI
 * Manager owns both now. The hold-a-draft-while-focused + `validate` contract is
 * a documented design-system capability (packages/ui/CLAUDE.md) and the next
 * expanded-editor field that declines a value belongs on it.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function DraftField({
  value,
  onChange,
  multiline,
  disabled,
  validate,
  id,
  targetKey,
}: DraftFieldProps): ReactElement {
  const [draft, setDraft] = useState(value);
  const focusedRef = useRef(false);
  const messageId = useId();
  useEffect(() => {
    if (!focusedRef.current) setDraft(value);
  }, [value]);
  // PB-4923 AC5: the lease rides the SAME focus lifetime the draft already
  // tracks. That is the point — the draft exists precisely while the person is
  // composing something the server cannot see yet.
  const lease = useFieldEditSession(targetKey);
  const onFocus = (): void => {
    focusedRef.current = true;
    lease.onFocus();
  };
  const onBlur = (): void => {
    focusedRef.current = false;
    setDraft(value);
    lease.onBlur();
  };
  // Derived from the draft, not stored: the message must clear on the keystroke
  // that fixes the value, and blur re-seeds the draft from the committed value,
  // which is valid by construction.
  const message = validate ? validate(draft) : null;
  const invalidProps = message
    ? { "aria-invalid": true, "aria-describedby": messageId }
    : {};
  // Matches the Input atom's invalid treatment (`border-pb-red/60`, full-strength
  // on focus) so the two do not drift apart.
  const invalidClass = "border-pb-red/60 focus:border-pb-red";
  const control = multiline ? (
    <textarea
      id={id}
      value={draft}
      disabled={disabled}
      rows={3}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(e.target.value);
      }}
      className={cn(inputClass, "resize-none", message && invalidClass)}
      {...invalidProps}
    />
  ) : (
    <input
      id={id}
      value={draft}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(e.target.value);
      }}
      className={cn(inputClass, message && invalidClass)}
      {...invalidProps}
    />
  );
  if (!message) return control;
  return (
    <>
      {control}
      {/* role="alert" so the reason is announced when it appears mid-edit, not
          only when the field is re-read. */}
      <p id={messageId} role="alert" className="text-dense-1 text-pb-red-text">
        {message}
      </p>
    </>
  );
}

function IconButton({
  children,
  onClick,
  disabled,
  label,
  ariaExpanded,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean | undefined;
  label: string;
  ariaExpanded?: boolean | undefined;
}): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-expanded={ariaExpanded}
      className="inline-flex items-center gap-1.5 rounded-md border border-pb-control-border bg-pb-card px-2.5 py-1.5 text-xs font-medium text-pb-text-secondary transition-colors hover:text-pb-text hover:pb-overlay-paint active:pb-overlay-paint-strong focus-ring disabled:opacity-50 disabled:pointer-events-none"
    >
      {children}
    </button>
  );
}

function TrashButton({
  onClick,
  disabled,
  label = "Delete row",
  className,
}: {
  onClick: () => void;
  disabled?: boolean | undefined;
  label?: string | undefined;
  className?: string | undefined;
}): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className={cn(
        "rounded-md p-1.5 text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring disabled:opacity-50 disabled:pointer-events-none",
        className,
      )}
    >
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * PB-4923 AC5 — the two target keys an expanded editor ever needs, built with
 * the SHARED grammar rather than a string template.
 *
 * `widgetTargetKey` is what the executor, the revision map, the edit-session
 * table and the audit all key on. A hand-written `` `points[${id}]/label` ``
 * would be right today and wrong the first time that grammar gains a case — and
 * it would fail SILENTLY, because a key nothing matches simply leases a target
 * no operation ever names, which looks exactly like working.
 */
function propTarget(name: string): string {
  return widgetTargetKey([{ kind: "prop", name }]);
}

function itemFieldTarget(
  collection: string,
  itemId: string,
  name: string,
): string {
  return widgetTargetKey([
    { kind: "item", collection, itemId },
    { kind: "prop", name },
  ]);
}

/** A leaf one collection deeper — `branches[br-1]/causes[c-2]/text`. */
function nestedItemFieldTarget(
  collection: string,
  itemId: string,
  childCollection: string,
  childId: string,
  name: string,
): string {
  return widgetTargetKey([
    { kind: "item", collection, itemId },
    { kind: "item", collection: childCollection, itemId: childId },
    { kind: "prop", name },
  ]);
}

const inputClass =
  "w-full rounded-md border border-pb-border bg-pb-page/40 px-2.5 py-1.5 text-xs text-pb-text focus-ring disabled:opacity-60 disabled:cursor-not-allowed";
